<script setup lang="ts">
import { usePointerRowDrag } from "../composables/usePointerRowDrag";
import { reorderExactSlotByKey } from "../lib/reorderExactSlot";
import {
  mapPage,
  clampMapDimension,
  MAP_FRAME_OPTIONS,
  pointOnMap,
} from "../lib/mapPage";
import type { MapPage } from "../lib/generated/zwMap";
import { useTheme } from "../composables/useTheme";
import ZwSelect from "./ui/ZwSelect.vue";
import MapPageSizeFields from "./MapPageSizeFields.vue";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  andThenMaybe,
  atIndexMaybe,
  unwrapMaybeOr,
  fromMaybe,
  type Maybe,
} from "../lib/maybe";
import { trimmedMaybe } from "../lib/trimmedString";
import type { MapEditorPort, MapEditorProps } from "./MapEditor.port";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
  type Component,
} from "vue";
import { databaseEntryPreview } from "../lib/databaseEntryPreview";
import { readProjectFile } from "../lib/tauri/core";
import { PROJECT_ASSETS_DIR, PROJECT_DATABASE_DIR } from "../lib/zwConfig";
import { entityDisplayName } from "../editor/entityOccurrences";
import ZwButton from "./ui/ZwButton.vue";
import StaticSvgMarkup from "./ui/StaticSvgMarkup.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwDropdown from "./ui/ZwDropdown.vue";
import ZwSearchableMenu from "./ui/ZwSearchableMenu.vue";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import ZwRangePopover from "./ui/ZwRangePopover.vue";
import MapLandmarkPanel from "./MapLandmarkPanel.vue";
import MapStoryPathSidebar from "./MapStoryPathSidebar.vue";
import {
  cloneMapDocument,
  createDefaultMapDocument,
  isMapFileName,
  LABEL_BASE_PX,
  MAP_LABEL_FONTS,
  LANDMARK_SIZE_MAX,
  isLabelsLocked,
  isLabelsVisible,
  isPaperLocked,
  isPaperVisible,
  mapPaperColor,
  MAP_STORY_PATH_COLOR,
  newMapId,
  parseMapDocumentWithHeal,
  serializeMapDocument,
  type MapDocument,
  type MapLandmark,
  type MapLayer,
} from "../lib/mapDocument";
import {
  appendSceneLinks,
  buildStoryPath,
  createStoryMarker,
  findStoryMarkerAt,
  placesForScene,
  primaryLandmarkForScene,
  removeSceneLinks,
  type StoryPlaceHover,
  type StoryPlaceRef,
} from "../lib/mapStoryPath";
import {
  storyPathPlaceKey,
  storyPathStopHitRadiusWorld,
} from "../lib/mapStoryPathDraw";
import { hitTestLandmarkMark, rotatePoint } from "../lib/mapLandmarkTransform";
import { POINTER_ROW_DRAG_THRESHOLD_PX } from "../composables/usePointerRowDrag";
import {
  landmarkDrawSize,
  screenToWorld,
  worldToScreen,
} from "../lib/mapGeometry";
import { entityChipAccentStyle } from "../lib/entityColors";
import {
  assetNameFromSource,
  assetRelativePath,
  formatErrorMessage,
} from "../lib/projectAssets";
import {
  importProjectAsset,
  listProjectAssets,
  pickImageFile,
  readProjectAssetDataUrl,
  writeProjectAssetBytes,
} from "../lib/tauri/assets";
import { isTauri, joinProjectPath } from "../lib/runtime";
import { saveMapPng, saveMapSvg } from "../lib/tauri/export";
import {
  dismissExportToast,
  reportSavedExport,
  reportExportFailed,
} from "../composables/useExportToast";
import { formatExportFailure } from "../composables/useExportToast.update";
import { mapSvgPixelSize, mapToSvg } from "../lib/mapToSvg";
import { rasterizeSvgToPngBytes } from "../lib/mapRasterize";
import { displayFileName } from "../lib/fileNames";
import {
  filterLandmarkGroups,
  landmarkIconById,
} from "../lib/mapLandmarkIcons";
import {
  canvasToPngDataUrl,
  dataUrlToBase64,
  TERRAIN_BRUSH_OPTIONS,
  formatLandmassCoastRoughness,
  isLandmassLocked,
  isLandmassVisible,
  isMapLandmassAssetPath,
  LANDMASS_BRUSH_SIZE_MAX,
  LANDMASS_BRUSH_SIZE_MIN,
  LANDMASS_COAST_ROUGHNESS_MAX,
  LANDMASS_COAST_ROUGHNESS_MIN,
} from "../lib/mapLandmass";
import {
  IconEye,
  IconEyeOff,
  IconImage,
  IconKeyboard,
  IconLock,
  IconMapEraser,
  IconMapExport,
  IconMapFill,
  IconMapStoryPath,
  IconMapLandmass,
  IconMapLine,
  IconMapPin,
  IconMapSelect,
  IconMapCrop,
  IconMapTerrain,
  IconMapType,
  IconMapWater,
  IconSettings,
  IconTrash,
  IconUnlock,
} from "./ribbonIcons";
import MapInkPicker from "./MapInkPicker.vue";
import { useMapTools, type MapTool } from "../composables/useMapTools";

const MAP_TOOLS: { id: MapTool; title: string; icon: Component }[] = [
  { id: "select", title: "Select", icon: IconMapSelect },
  { id: "crop", title: "Crop", icon: IconMapCrop },
  { id: "landmass", title: "Land", icon: IconMapLandmass },
  { id: "water", title: "Water", icon: IconMapWater },
  { id: "terrain", title: "Terrain", icon: IconMapTerrain },
  { id: "line", title: "Line", icon: IconMapLine },
  { id: "fill", title: "Fill", icon: IconMapFill },
  { id: "landmark", title: "Landmark", icon: IconMapPin },
  { id: "image", title: "Assets", icon: IconImage },
  { id: "storyPath", title: "Story path", icon: IconMapStoryPath },
  { id: "erase", title: "Erase", icon: IconMapEraser },
  { id: "label", title: "Label", icon: IconMapType },
];

const symbolSearch = ref("");
const symbolGroups = computed(() => filterLandmarkGroups(symbolSearch.value));
const assetList = ref<string[]>([]);
/** Data URLs for Assets panel thumbnails (path → preview). */
const assetPreviews = shallowRef<Record<string, string>>({});
const assetUploadInput = useTemplateRef<HTMLInputElement>("assetUploadInput");
const assetBusy = ref(false);

const model = defineModel<string>({ default: "" });

const props = defineProps<MapEditorProps>();

const canvasEl = useTemplateRef<HTMLCanvasElement>("canvasEl");
const canvas = computed(() => toMaybe(canvasEl.value));
const sidebarEl = useTemplateRef<HTMLElement>("sidebarEl");
const shellEl = useTemplateRef<HTMLElement>("shellEl");

const doc = ref<MapDocument>(createDefaultMapDocument());
const renamingLayerId = ref<Maybe<string>>(nothing);
const renameLayerDraft = ref("");
const renameLayerInput =
  shallowRef<Maybe<InstanceType<typeof ZwInput>>>(nothing);
const labelInput = useTemplateRef<HTMLTextAreaElement>("labelInput");
const exportBusy = ref(false);
const pageSettingsOpen = ref(false);
const pageWidth = ref(2400);
const pageHeight = ref(1600);
const pageFrame = ref<MapPage["frame"]>("none");
const { theme } = useTheme();

function openPageSettings(): void {
  const page = mapPage(doc.value);
  pageWidth.value = page.width;
  pageHeight.value = page.height;
  pageFrame.value = page.frame;
  pageSettingsOpen.value = true;
}
function fitMapPage(): void {
  const canvas = toMaybe(canvasEl.value);
  if (isNothing(canvas)) return;
  const bounds = canvas.value.getBoundingClientRect();
  const page = mapPage(doc.value);
  const sidebarWidth = sidebarEl.value?.getBoundingClientRect().width ?? 0;
  const zoom = Math.max(
    0.01,
    Math.min(
      1,
      (bounds.width - sidebarWidth - 64) / page.width,
      (bounds.height - 64) / page.height,
    ),
  );
  doc.value.viewport = {
    x: (page.centerX ?? 0) + sidebarWidth / (2 * zoom),
    y: page.centerY ?? 0,
    zoom,
  };
  scheduleDraw();
}
function applyPageSettings(): void {
  const page = {
    ...mapPage(doc.value),
    width: clampMapDimension(pageWidth.value),
    height: clampMapDimension(pageHeight.value),
    frame: pageFrame.value,
  };
  if (JSON.stringify(page) !== JSON.stringify(mapPage(doc.value))) {
    pushUndo();
    doc.value.page = page;
    scheduleDraw();
  }
  pageSettingsOpen.value = false;
}

const readonly = computed(() => props.readonly === true);

/** Place keys highlighted from Story path sidebar hover of placed rows. */
const storySidebarHighlightKeys = shallowRef<readonly string[]>([]);

const {
  tool,
  selection,
  inkColor,
  fillColor,
  inkWeight,
  inkStyle,
  panning,
  spaceHeld,
  landmarkStamp,
  imageStamp,
  labelDraft,
  labelDialogOpen,
  labelEditorStyle,
  editingLabel,
  shortcutsOpen,
  inkTargetSelected,
  fillTargetSelected,
  sizeSliderKind,
  objectSizeMin,
  showResizeCursor,
  resizeCursor,
  sizeSliderModel,
  landmassBrushSize,
  landmassCoastRoughness,
  terrainBrushStyle,
  terrainOpacity,
  terrainCoverage,
  waterMode,
  labelSize,
  labelFont,
  labelRotation,
  labelCurve,
  labelSpacing,
  applyLabelAppearance,
  pushUndo,
  selectedLandmark,
  selectLandmarkById,
  storyPathHover,
  storyPathSelection,
  cancelDraftLine,
  setTool,
  scheduleDraw,
  applyInkColor,
  applyFillColor,
  applyInkWeight,
  applyInkStyle,
  splitSelectedNode,
  toggleShortcuts,
  closeShortcuts,
  cancelLabelDialog,
  commitLabel,
  setLandmassFromImage,
  getLandmassCanvas,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onWheel,
  onDblClick,
  onContextMenu,
  onKeyDown,
  onKeyUp,
  layerById,
  cleanup: cleanupMapTools,
} = useMapTools({
  doc,
  canvasEl: canvas,
  rootPath: computed(() => props.rootPath),
  readonly,
  sceneOrder: computed(() =>
    props.manuscriptChapters.flatMap((chapter) =>
      chapter.scenes.map((scene) => scene.path),
    ),
  ),
  arcMoments: computed(() => props.arcMoments),
  storyHighlightKeys: storySidebarHighlightKeys,
  onLandmassDirty: () => {
    schedulePersistLandmass();
  },
  onStoryNodeDragStart: (payload) => {
    beginStoryNodeDragFromMap(payload);
  },
});

/** Filled after story pointer-drag helpers are defined (useMapTools runs first). */
let beginStoryNodeDragFromMap: (payload: {
  paths: readonly string[];
  from: StoryPlaceRef;
  event: PointerEvent;
}) => void = () => {};

function isStoryScenePlaced(path: string): boolean {
  return placesForScene(doc.value, path).length > 0;
}

function storyLandmarkIconMarkup(path: string): string {
  return matchMaybe(primaryLandmarkForScene(doc.value, path), {
    nothing: () => "",
    just: (landmark) => {
      const iconId = trimmedMaybe(landmark.iconId);
      if (isNothing(iconId)) return "";
      return matchMaybe(landmarkIconById(iconId.value), {
        nothing: () => "",
        just: (icon) => fromMaybe(trimmedMaybe(icon.svg)) ?? "",
      });
    },
  });
}

function onStorySidebarHoverPaths(paths: readonly string[]): void {
  if (paths.length === 0) {
    if (storySidebarHighlightKeys.value.length === 0) return;
    storySidebarHighlightKeys.value = [];
    scheduleDraw();
    return;
  }
  const keys = new Set<string>();
  for (const path of paths) {
    for (const place of placesForScene(doc.value, path)) {
      keys.add(storyPathPlaceKey(place.kind, place.id));
    }
  }
  storySidebarHighlightKeys.value = [...keys];
  scheduleDraw();
}

/**
 * Keep the last hover while the pointer is over the floating card/caret.
 * Canvas leave clears cursorWorld before the pointer can enter the overlay, so
 * we latch with a short grace period across that handoff.
 */
const storyHoverOverCard = ref(false);
const storyHoverHasFocus = ref(false);
const storyLocationMenuOpen = ref(false);
const storyHoverLatch = shallowRef<Maybe<StoryPlaceHover>>(nothing);
let storyHoverClearTimer: Maybe<ReturnType<typeof setTimeout>> = nothing;

function clearStoryHoverClearTimer(): void {
  if (isNothing(storyHoverClearTimer)) return;
  clearTimeout(storyHoverClearTimer.value);
  storyHoverClearTimer = nothing;
}

function scheduleStoryHoverClear(): void {
  clearStoryHoverClearTimer();
  storyHoverClearTimer = just(
    setTimeout(() => {
      storyHoverClearTimer = nothing;
      if (
        storyHoverOverCard.value === false &&
        storyHoverHasFocus.value === false &&
        storyLocationMenuOpen.value === false &&
        isNothing(storyPathHover.value)
      ) {
        storyHoverLatch.value = nothing;
      }
    }, 160),
  );
}

function onStoryHoverAnchorEnter(): void {
  clearStoryHoverClearTimer();
  storyHoverOverCard.value = true;
}

function onStoryHoverAnchorLeave(): void {
  storyHoverOverCard.value = false;
  if (isNothing(storyPathHover.value)) {
    scheduleStoryHoverClear();
  }
}

watch(storyPathHover, (hover) => {
  if (storyHoverHasFocus.value || storyLocationMenuOpen.value) return;
  if (isJust(hover)) {
    clearStoryHoverClearTimer();
    storyHoverLatch.value = hover;
    return;
  }
  if (storyHoverOverCard.value === false) {
    scheduleStoryHoverClear();
  }
});

watch(tool, (next) => {
  if (next === "storyPath") return;
  clearStoryHoverClearTimer();
  storyHoverOverCard.value = false;
  storyHoverHasFocus.value = false;
  storyLocationMenuOpen.value = false;
  storyHoverLatch.value = nothing;
  if (storySidebarHighlightKeys.value.length > 0) {
    storySidebarHighlightKeys.value = [];
  }
});

const storyHoverCard = computed((): Maybe<StoryPlaceHover> => {
  if (tool.value !== "storyPath") return nothing;
  if (isJust(storyPointerDrag.value)) return nothing;
  if (isJust(storyPathSelection.value)) return storyPathSelection.value;
  if (
    (storyHoverHasFocus.value || storyLocationMenuOpen.value) &&
    isJust(storyHoverLatch.value)
  )
    return storyHoverLatch.value;
  const live = storyPathHover.value;
  if (isJust(live) && live.value.sceneFiles.length > 0) return live;
  const latched = storyHoverLatch.value;
  if (isJust(latched) && latched.value.sceneFiles.length > 0) return latched;
  return nothing;
});

const storyHoverCardStyle = computed((): Record<string, string> => {
  const hover = storyHoverCard.value;
  if (isNothing(hover)) return {};
  const el = canvasEl.value;
  if (el === null) return {};
  const screen = worldToScreen(
    { x: hover.value.place.x, y: hover.value.place.y },
    doc.value.viewport,
    el.clientWidth,
    el.clientHeight,
  );
  return {
    left: `${screen.x}px`,
    top: `${screen.y}px`,
  };
});

/** Landmark caption when present; otherwise no title (no "Landmark" / "Free marker"). */
function storyHoverTitle(hover: StoryPlaceHover): Maybe<string> {
  if (hover.place.kind !== "landmark") return nothing;
  for (const layer of doc.value.layers) {
    const landmark = layer.landmarks.find((lm) => lm.id === hover.place.id);
    if (typeof landmark === "undefined") continue;
    const captionM = toMaybe(landmark.caption);
    if (isJust(captionM)) {
      const trimmed = captionM.value.trim();
      if (trimmed.length > 0) return just(trimmed);
    }
    return nothing;
  }
  return nothing;
}

function storyHoverAriaLabel(hover: StoryPlaceHover): string {
  const title = storyHoverTitle(hover);
  if (isJust(title)) return title.value;
  const first = hover.sceneFiles[0];
  if (typeof first === "string") return storySceneLabel(first);
  return "Story path place";
}

const storyHoverCardTitle = computed((): Maybe<string> => {
  const hover = storyHoverCard.value;
  if (isNothing(hover)) return nothing;
  return storyHoverTitle(hover.value);
});

function storyArcChipLabel(arc: {
  entityFile: string;
  momentLabel: string;
}): string {
  return `${displayFileName(arc.entityFile)} · ${arc.momentLabel}`;
}

function storyArcChipClass(arc: { color: Maybe<string> }): string {
  return isNothing(arc.color)
    ? "map-story-hover-chip map-story-hover-chip-default"
    : "map-story-hover-chip";
}

function storyArcChipStyle(arc: {
  color: Maybe<string>;
}): Maybe<Record<string, string>> {
  return entityChipAccentStyle(arc.color);
}

function openStoryEntity(fileName: string): void {
  const open = props.onSelectEntityFile;
  if (isJust(open)) open.value(fileName);
}

function storySceneLabel(path: string): string {
  const opt = props.sceneOptions.find((o) => o.value === path);
  if (typeof opt !== "undefined") return opt.label;
  for (const chapter of props.manuscriptChapters) {
    const scene = chapter.scenes.find((s) => s.path === path);
    if (typeof scene !== "undefined") return scene.label;
  }
  return displayFileName(path);
}

function openStoryScene(path: string): void {
  if (suppressNextStorySceneOpen) {
    suppressNextStorySceneOpen = false;
    return;
  }
  const open = props.onSelectSceneFile;
  if (isJust(open)) open.value(path);
}

type StoryPointerDrag = {
  paths: string[];
  label: string;
  clientX: number;
  clientY: number;
  /** 1-based path index when relocating an existing stop; nothing for new drops. */
  stopIndex: Maybe<number>;
  /** When set, scenes are relocated from this place instead of only appended. */
  from: Maybe<StoryPlaceRef>;
};

type StoryDragCandidate = {
  paths: string[];
  label: string;
  startX: number;
  startY: number;
  stopIndex: Maybe<number>;
  from: Maybe<StoryPlaceRef>;
};

const storyPointerDrag = shallowRef<Maybe<StoryPointerDrag>>(nothing);
let storyDragCandidate: Maybe<StoryDragCandidate> = nothing;
/** Suppress scene open click that follows a completed pointer drag. */
let suppressNextStorySceneOpen = false;

function canvasWorldFromClient(
  clientX: number,
  clientY: number,
): Maybe<{ x: number; y: number }> {
  const el = canvasEl.value;
  if (el === null) return nothing;
  const bounds = el.getBoundingClientRect();
  if (
    clientX < bounds.left ||
    clientX > bounds.right ||
    clientY < bounds.top ||
    clientY > bounds.bottom
  ) {
    return nothing;
  }
  const screen = {
    x: clientX - bounds.left,
    y: clientY - bounds.top,
  };
  const world = screenToWorld(
    screen,
    doc.value.viewport,
    bounds.width,
    bounds.height,
  );
  if (pointOnMap(doc.value, world) === false) return nothing;
  return just(world);
}

/** Generous screen-px pad so Story path drops stick to landmarks easily. */
const STORY_LANDMARK_DROP_TOLERANCE_PX = 20;
/** Fallback: nearest landmark center within this many screen pixels. */
const STORY_LANDMARK_NEAR_PX = 48;

function findLandmarkAtStoryDrop(world: {
  x: number;
  y: number;
}): Maybe<MapLandmark> {
  const el = canvasEl.value;
  if (el === null) return nothing;
  const zoom = doc.value.viewport.zoom;
  const width = el.clientWidth;
  const height = el.clientHeight;
  const dropScreen = worldToScreen(world, doc.value.viewport, width, height);
  let nearest: Maybe<{ landmark: MapLandmark; dist: number }> = nothing;

  for (const layer of doc.value.layers.toReversed()) {
    // Locked geography still accepts story links; only hide skips.
    if (layer.visible === false) continue;
    for (const lm of layer.landmarks.toReversed()) {
      // Screen-space hit matches what the author sees (incl. current zoom).
      const size = landmarkDrawSize(lm, zoom);
      const lmScreen = worldToScreen(
        { x: lm.x, y: lm.y },
        doc.value.viewport,
        width,
        height,
      );
      const local = rotatePoint(
        { x: dropScreen.x - lmScreen.x, y: dropScreen.y - lmScreen.y },
        -(lm.rotation ?? 0),
      );
      if (
        Math.abs(local.x) <=
          size.width / 2 + STORY_LANDMARK_DROP_TOLERANCE_PX &&
        Math.abs(local.y) <= size.height / 2 + STORY_LANDMARK_DROP_TOLERANCE_PX
      ) {
        return just(lm);
      }
      const dist = Math.hypot(
        dropScreen.x - lmScreen.x,
        dropScreen.y - lmScreen.y,
      );
      if (
        dist <= STORY_LANDMARK_NEAR_PX &&
        (isNothing(nearest) || dist < nearest.value.dist)
      ) {
        nearest = just({ landmark: lm, dist });
      }
    }
  }
  if (isJust(nearest)) return just(nearest.value.landmark);

  // World-space fallback (same helper the canvas pick uses).
  for (const layer of doc.value.layers.toReversed()) {
    if (layer.visible === false) continue;
    for (const lm of layer.landmarks.toReversed()) {
      if (
        hitTestLandmarkMark(lm, world, zoom, STORY_LANDMARK_DROP_TOLERANCE_PX)
      ) {
        return just(lm);
      }
    }
  }
  return nothing;
}

function detachScenesFromPlace(
  from: StoryPlaceRef,
  paths: readonly string[],
): void {
  if (from.kind === "landmark") {
    for (const layer of doc.value.layers) {
      const landmark = layer.landmarks.find((lm) => lm.id === from.id);
      if (typeof landmark === "undefined") continue;
      const next = removeSceneLinks(toMaybe(landmark.sceneLinks), paths);
      if (next.length === 0) delete landmark.sceneLinks;
      else landmark.sceneLinks = next;
      return;
    }
    return;
  }
  const marker = doc.value.storyMarkers.find((m) => m.id === from.id);
  if (typeof marker === "undefined") return;
  const next = removeSceneLinks(toMaybe(marker.sceneLinks), paths);
  if (next.length === 0) {
    doc.value.storyMarkers = doc.value.storyMarkers.filter(
      (m) => m.id !== from.id,
    );
    return;
  }
  marker.sceneLinks = next;
}

function placeStoryScenesAtWorld(
  paths: readonly string[],
  world: { x: number; y: number },
  from: Maybe<StoryPlaceRef>,
): void {
  if (paths.length === 0 || readonly.value === true) return;
  const zoom = doc.value.viewport.zoom;
  const radius = storyPathStopHitRadiusWorld(zoom);
  const landmarkM = findLandmarkAtStoryDrop(world);

  // Dropping back onto the same landmark is a no-op.
  if (
    isJust(from) &&
    from.value.kind === "landmark" &&
    isJust(landmarkM) &&
    landmarkM.value.id === from.value.id
  ) {
    return;
  }

  // Relocate a whole free marker onto blank map: move in place.
  // Landmarks win: dropping on a landmark associates rather than nudging.
  if (isJust(from) && from.value.kind === "marker" && isNothing(landmarkM)) {
    const marker = doc.value.storyMarkers.find((m) => m.id === from.value.id);
    if (typeof marker !== "undefined") {
      const linksM = toMaybe(marker.sceneLinks);
      const onMarkerFiles = new Set<string>();
      if (isJust(linksM)) {
        for (const link of linksM.value) {
          const file = link.file.trim();
          if (file.length > 0) onMarkerFiles.add(file);
        }
      }
      const movingAll =
        paths.length === onMarkerFiles.size &&
        paths.every((p) => onMarkerFiles.has(p));
      const otherMarkerM = findStoryMarkerAt(doc.value, world, radius);
      const overOtherMarker =
        isJust(otherMarkerM) && otherMarkerM.value.id !== from.value.id;
      if (movingAll === true && overOtherMarker === false) {
        // Same marker under the cursor: no-op.
        if (Math.hypot(world.x - marker.x, world.y - marker.y) <= radius) {
          return;
        }
        pushUndo();
        marker.x = world.x;
        marker.y = world.y;
        scheduleDraw();
        return;
      }
    }
  }

  // Dropping back onto the same free marker is a no-op.
  if (isJust(from) && from.value.kind === "marker" && isNothing(landmarkM)) {
    const marker = doc.value.storyMarkers.find((m) => m.id === from.value.id);
    if (
      typeof marker !== "undefined" &&
      Math.hypot(world.x - marker.x, world.y - marker.y) <= radius
    ) {
      return;
    }
  }

  pushUndo();
  if (isJust(from)) {
    detachScenesFromPlace(from.value, paths);
  }

  if (isJust(landmarkM)) {
    landmarkM.value.sceneLinks = appendSceneLinks(
      toMaybe(landmarkM.value.sceneLinks),
      paths,
    );
    scheduleDraw();
    return;
  }

  const markerM = findStoryMarkerAt(doc.value, world, radius);
  if (isJust(markerM)) {
    markerM.value.sceneLinks = appendSceneLinks(
      toMaybe(markerM.value.sceneLinks),
      paths,
    );
    scheduleDraw();
    return;
  }

  doc.value.storyMarkers.push(
    createStoryMarker({
      id: newMapId(),
      x: world.x,
      y: world.y,
      sceneFiles: paths,
    }),
  );
  scheduleDraw();
}

function clearStoryDragCandidate(): void {
  storyDragCandidate = nothing;
  window.removeEventListener("pointermove", onStoryCandidateMove);
  window.removeEventListener("pointerup", onStoryCandidateUp);
  window.removeEventListener("pointercancel", onStoryCandidateCancel);
}

function endStoryPointerDrag(clientX: number, clientY: number): void {
  const session = storyPointerDrag.value;
  storyPointerDrag.value = nothing;
  window.removeEventListener("pointermove", onStoryPointerMove);
  window.removeEventListener("pointerup", onStoryPointerUp);
  window.removeEventListener("pointercancel", onStoryPointerCancel);
  if (isNothing(session)) return;
  suppressNextStorySceneOpen = true;
  const worldM = canvasWorldFromClient(clientX, clientY);
  if (isNothing(worldM)) return;
  placeStoryScenesAtWorld(
    session.value.paths,
    worldM.value,
    session.value.from,
  );
}

function onStoryPointerMove(event: PointerEvent): void {
  if (isNothing(storyPointerDrag.value)) return;
  event.preventDefault();
  storyPointerDrag.value = just({
    ...storyPointerDrag.value.value,
    clientX: event.clientX,
    clientY: event.clientY,
  });
}

function onStoryPointerUp(event: PointerEvent): void {
  endStoryPointerDrag(event.clientX, event.clientY);
}

function onStoryPointerCancel(): void {
  storyPointerDrag.value = nothing;
  window.removeEventListener("pointermove", onStoryPointerMove);
  window.removeEventListener("pointerup", onStoryPointerUp);
  window.removeEventListener("pointercancel", onStoryPointerCancel);
}

function onStoryCandidateMove(event: PointerEvent): void {
  if (isNothing(storyDragCandidate)) return;
  const candidate = storyDragCandidate.value;
  const dx = event.clientX - candidate.startX;
  const dy = event.clientY - candidate.startY;
  if (Math.hypot(dx, dy) < POINTER_ROW_DRAG_THRESHOLD_PX) return;
  clearStoryDragCandidate();
  event.preventDefault();
  // Leave the hover card so it does not steal the drop target.
  clearStoryHoverClearTimer();
  storyHoverOverCard.value = false;
  storyHoverHasFocus.value = false;
  storyLocationMenuOpen.value = false;
  storyHoverLatch.value = nothing;
  storyPointerDrag.value = just({
    paths: candidate.paths,
    label: candidate.label,
    clientX: event.clientX,
    clientY: event.clientY,
    stopIndex: candidate.stopIndex,
    from: candidate.from,
  });
  window.addEventListener("pointermove", onStoryPointerMove, {
    passive: false,
  });
  window.addEventListener("pointerup", onStoryPointerUp);
  window.addEventListener("pointercancel", onStoryPointerCancel);
}

function onStoryCandidateUp(): void {
  clearStoryDragCandidate();
}

function onStoryCandidateCancel(): void {
  clearStoryDragCandidate();
}

function storyStopIndexForPlace(from: StoryPlaceRef): Maybe<number> {
  const order = props.manuscriptChapters.flatMap((chapter) =>
    chapter.scenes.map((scene) => scene.path),
  );
  const stop = buildStoryPath(doc.value, order).find(
    (s) => s.kind === from.kind && s.id === from.id,
  );
  return typeof stop === "undefined" ? nothing : just(stop.index);
}

function onStoryStartDrag(payload: {
  paths: string[];
  label: string;
  event: PointerEvent;
  from?: StoryPlaceRef;
}): void {
  if (tool.value !== "storyPath" || readonly.value === true) return;
  if (payload.paths.length === 0) return;
  // pointerdown: button === 0. pointermove (map threshold): button is -1.
  const primaryDown =
    payload.event.button === 0 || (payload.event.buttons & 1) === 1;
  if (primaryDown === false) return;
  clearStoryDragCandidate();
  onStoryPointerCancel();
  const from =
    typeof payload.from === "undefined" ? nothing : just(payload.from);
  storyDragCandidate = just({
    paths: payload.paths,
    label: payload.label,
    startX: payload.event.clientX,
    startY: payload.event.clientY,
    stopIndex: isJust(from) ? storyStopIndexForPlace(from.value) : nothing,
    from,
  });
  window.addEventListener("pointermove", onStoryCandidateMove, {
    passive: false,
  });
  window.addEventListener("pointerup", onStoryCandidateUp);
  window.addEventListener("pointercancel", onStoryCandidateCancel);
}

/** Canvas node drag already passed the pending threshold: start the ghost now. */
function beginActiveStoryPointerDrag(payload: {
  paths: string[];
  label: string;
  event: PointerEvent;
  from: StoryPlaceRef;
}): void {
  if (tool.value !== "storyPath" || readonly.value === true) return;
  if (payload.paths.length === 0) return;
  clearStoryDragCandidate();
  onStoryPointerCancel();
  clearStoryHoverClearTimer();
  storyHoverOverCard.value = false;
  storyHoverHasFocus.value = false;
  storyLocationMenuOpen.value = false;
  storyHoverLatch.value = nothing;
  const from = just(payload.from);
  storyPointerDrag.value = just({
    paths: payload.paths,
    label: payload.label,
    clientX: payload.event.clientX,
    clientY: payload.event.clientY,
    stopIndex: storyStopIndexForPlace(payload.from),
    from,
  });
  window.addEventListener("pointermove", onStoryPointerMove, {
    passive: false,
  });
  window.addEventListener("pointerup", onStoryPointerUp);
  window.addEventListener("pointercancel", onStoryPointerCancel);
}

beginStoryNodeDragFromMap = (payload) => {
  const paths = [...payload.paths];
  const first = paths[0];
  beginActiveStoryPointerDrag({
    paths,
    label:
      paths.length === 1 && typeof first === "string"
        ? storySceneLabel(first)
        : `${paths.length} scenes`,
    event: payload.event,
    from: payload.from,
  });
};

const storyDragGhostStyle = computed((): Record<string, string> => {
  const drag = storyPointerDrag.value;
  if (isNothing(drag)) return {};
  return {
    left: `${drag.value.clientX}px`,
    top: `${drag.value.clientY}px`,
    "--map-story-drag-node": MAP_STORY_PATH_COLOR,
  };
});

const storyDragGhostIndex = computed((): string => {
  const drag = storyPointerDrag.value;
  if (isNothing(drag)) return "";
  if (isJust(drag.value.stopIndex)) return String(drag.value.stopIndex.value);
  return drag.value.paths.length > 1 ? String(drag.value.paths.length) : "·";
});

const labelPixelSize = computed({
  get: () => Math.round(labelSize.value * LABEL_BASE_PX),
  set: (value: number) => {
    sizeSliderModel.value = value / LABEL_BASE_PX;
  },
});
const showLabelOptions = computed(
  () =>
    tool.value === "label" ||
    (isJust(selection.value) && selection.value.value.kind === "label"),
);
const LABEL_FONT_OPTIONS = [
  { value: "cartographic", label: "Kauffer Hand" },
  { value: "kauffer-italic", label: "Kauffer Italic" },
  { value: "kauffer-title", label: "Kauffer Title" },
  { value: "myer-scratch", label: "Myer Scratch" },
] as const;
const selectedFontName = computed(() => {
  const option = toMaybe(
    LABEL_FONT_OPTIONS.find((font) => font.value === labelFont.value),
  );
  return isJust(option) ? option.value.label : labelFont.value;
});

function onLabelEnter(event: KeyboardEvent): void {
  if (event.isComposing || event.shiftKey) return;
  event.preventDefault();
  commitLabel();
}

watch(
  [
    labelDialogOpen,
    labelDraft,
    labelFont,
    labelSize,
    labelRotation,
    labelCurve,
    labelSpacing,
    inkColor,
  ],
  scheduleDraw,
);

const hasToolOptions = computed(() => {
  if (readonly.value || tool.value === "crop" || tool.value === "storyPath")
    return false;
  // Assets tool chrome is the library panel; placed images have no tool fields.
  if (tool.value === "image") return false;
  if (tool.value === "select") {
    if (isNothing(selection.value)) return false;
    if (selection.value.value.kind === "image") return false;
    return true;
  }
  return true;
});

const landmassBrushSizeText = computed(() =>
  String(Math.round(landmassBrushSize.value)),
);
const landmassCoastRoughnessText = computed(() =>
  formatLandmassCoastRoughness(landmassCoastRoughness.value),
);
const WATER_MODE_OPTIONS = [
  { value: "lake", label: "Lake" },
  { value: "river", label: "River" },
] as const;

const showPathOptions = computed(
  () =>
    tool.value === "line" ||
    (isJust(selection.value) &&
      (selection.value.value.kind === "path" ||
        selection.value.value.kind === "node")),
);
const PATH_STYLE_OPTIONS = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
] as const;

const PATH_WEIGHT_OPTIONS = [
  { value: "thin", label: "Thin" },
  { value: "medium", label: "Medium" },
  { value: "thick", label: "Thick" },
] as const;
const objectSizeText = computed(() => `${sizeSliderModel.value.toFixed(2)}×`);
const objectSizeLabel = computed(() =>
  isJust(sizeSliderKind.value) && sizeSliderKind.value.value === "label"
    ? "Label"
    : "Size",
);

let landmassPersistTimer: Maybe<ReturnType<typeof setTimeout>> = nothing;

async function persistLandmassMask(): Promise<void> {
  const lm = doc.value.landmass;
  const canvasM = getLandmassCanvas();
  if (
    typeof lm === "undefined" ||
    isNothing(canvasM) ||
    isNothing(props.rootPath) ||
    props.rootPath.value.length === 0 ||
    isTauri() === false
  ) {
    return;
  }
  try {
    const dataUrl = canvasToPngDataUrl(canvasM.value);
    await writeProjectAssetBytes(
      props.rootPath.value,
      lm.path,
      dataUrlToBase64(dataUrl),
    );
  } catch (error) {
    console.error("Failed to save landmass mask", error);
  }
}

function schedulePersistLandmass(): void {
  if (isJust(landmassPersistTimer)) clearTimeout(landmassPersistTimer.value);
  landmassPersistTimer = just(
    setTimeout(() => {
      landmassPersistTimer = nothing;
      void persistLandmassMask();
    }, 400),
  );
}

const activeLandmark = computed(() => selectedLandmark());
const activeLandmarkEntityFile = computed(() =>
  andThenMaybe(activeLandmark.value, (landmark) =>
    toMaybe(landmark.entityFile),
  ),
);

const markerLocationOptions = computed(() => [
  { value: "", label: "No location" },
  ...props.entityOptions.filter(
    (entry) => isJust(entry.type) && entry.type.value === "location",
  ),
]);
function setStoryMarkerLocation(id: string, value: string): void {
  if (readonly.value) return;
  const marker = toMaybe(doc.value.storyMarkers.find((item) => item.id === id));
  if (isNothing(marker) || (marker.value.entityFile ?? "") === value) return;
  pushUndo();
  if (value.length === 0) delete marker.value.entityFile;
  else marker.value.entityFile = value;
  scheduleDraw();
}
const hoverMarkerLocation = computed({
  get: () => {
    const hover = storyHoverCard.value;
    if (isNothing(hover) || hover.value.place.kind !== "marker") return "";
    const id = hover.value.place.id;
    return (
      doc.value.storyMarkers.find((marker) => marker.id === id)?.entityFile ??
      ""
    );
  },
  set: (value: string) => {
    const hover = storyHoverCard.value;
    if (isJust(hover) && hover.value.place.kind === "marker")
      setStoryMarkerLocation(hover.value.place.id, value);
  },
});

const entityOptions = computed(() => props.entityOptions);
const sceneOptions = computed(() => props.sceneOptions);
const manuscriptChapters = computed(() => props.manuscriptChapters);

const landmarkEntityResolved = computed(() => {
  const fileM = activeLandmarkEntityFile.value;
  if (isNothing(fileM) || fileM.value.length === 0) return true;
  // Maps are never valid worldbuilding targets.
  if (isMapFileName(fileM.value)) return false;
  // Avoid a broken flash while the shell has not passed options yet.
  if (entityOptions.value.length === 0) return true;
  return entityOptions.value.some((opt) => opt.value === fileM.value);
});

const boundEntityTitle = ref("");
/** HTML body for the bound entry (entity-peek style). Empty when missing. */
const boundEntityBodyHtml = ref("");
let boundEntityLoadToken = 0;

watch(
  () => ({
    file: activeLandmarkEntityFile.value,
    root: props.rootPath,
    resolved: landmarkEntityResolved.value,
  }),
  async ({ file, root, resolved }) => {
    const token = ++boundEntityLoadToken;
    boundEntityTitle.value = "";
    boundEntityBodyHtml.value = "";
    const fileM = file;
    if (isNothing(fileM) || fileM.value.length === 0) return;
    const name = fileM.value;
    boundEntityTitle.value = displayFileName(name);
    if (isMapFileName(name) || resolved === false) return;
    const rootM = root;
    if (isNothing(rootM) || rootM.value.length === 0) return;
    try {
      const raw = await readProjectFile(
        joinProjectPath(rootM.value, `${PROJECT_DATABASE_DIR}/${name}`),
      );
      if (token !== boundEntityLoadToken) return;
      const preview = databaseEntryPreview(raw, name);
      if (preview.tag !== "just") {
        // Linked file is not a worldbuilding entry (e.g. stale map bind).
        return;
      }
      boundEntityTitle.value = entityDisplayName(raw, name);
      boundEntityBodyHtml.value = preview.value.bodyHtml;
    } catch {
      if (token !== boundEntityLoadToken) return;
      boundEntityBodyHtml.value = "";
    }
  },
  { immediate: true },
);

/** One undo frame per selection for panel field edits. */
let landmarkPanelUndoPushed = false;
watch(activeLandmark, () => {
  landmarkPanelUndoPushed = false;
});

function beginLandmarkPanelEdit(): void {
  if (readonly.value || landmarkPanelUndoPushed) return;
  pushUndo();
  landmarkPanelUndoPushed = true;
}

function setLandmarkCaption(value: string): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  if (value.trim().length > 0) lm.caption = value;
  else delete lm.caption;
}

function setLandmarkNotes(value: string): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  if (value.trim().length > 0) lm.notes = value;
  else delete lm.notes;
}

function setLandmarkEntityFile(value: string): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  const trimmed = value.trim();
  if (trimmed.length === 0 || isMapFileName(trimmed)) {
    delete lm.entityFile;
    return;
  }
  lm.entityFile = trimmed;
}

function clearLandmarkEntity(): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  delete lm.entityFile;
}

function openLandmarkEntry(): void {
  const fileM = activeLandmarkEntityFile.value;
  if (isNothing(fileM) || fileM.value.length === 0) return;
  if (isJust(props.onSelectEntityFile))
    props.onSelectEntityFile.value(fileM.value);
}

function addLandmarkSceneLink(file: string): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  const trimmed = file.trim();
  if (trimmed.length === 0) return;
  const existing = lm.sceneLinks ?? [];
  if (existing.some((link) => link.file === trimmed)) return;
  lm.sceneLinks = [...existing, { file: trimmed }];
}

function replaceLandmarkSceneLink(index: number, file: string): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  const links = toMaybe(lm.sceneLinks);
  if (isNothing(links)) return;
  const link = atIndexMaybe(links.value, index);
  if (isNothing(link)) return;
  const trimmed = file.trim();
  if (trimmed.length === 0) return;
  if (links.value.some((other, i) => i !== index && other.file === trimmed)) {
    return;
  }
  link.value.file = trimmed;
}

function removeLandmarkSceneLink(index: number): void {
  const landmark = selectedLandmark();
  if (isNothing(landmark)) return;
  const lm = landmark.value;
  const links = toMaybe(lm.sceneLinks);
  if (isNothing(links)) return;
  lm.sceneLinks = links.value.filter((_, i) => i !== index);
  if (lm.sceneLinks.length === 0) delete lm.sceneLinks;
}

function openLandmarkScene(file: string): void {
  const trimmed = file.trim();
  if (trimmed.length === 0) return;
  if (isJust(props.onSelectSceneFile)) props.onSelectSceneFile.value(trimmed);
}

function closeLandmarkPanel(): void {
  selection.value = nothing;
}

function onLayerNameClick(layer: MapLayer): void {
  setActiveLayer(layer.id);
  if (readonly.value) return;
  void startRenameLayer(layer);
}

function toggleLayerVisible(layer: MapLayer): void {
  layer.visible = layer.visible === false;
  scheduleDraw();
}

function toggleLayerLocked(layer: MapLayer): void {
  layer.locked = layer.locked === false;
}

/** Select a queued landmark after the map JSON is in `doc` (not before). */
function tryApplyFocusLandmark(): void {
  const idM = props.focusLandmarkId;
  if (isNothing(idM) || idM.value.length === 0) return;
  if (selectLandmarkById(idM.value) === false) return;
  if (isJust(props.onFocusLandmarkApplied))
    props.onFocusLandmarkApplied.value();
}

const isApplePlatform =
  typeof navigator !== "undefined" &&
  (/Mac|iPhone|iPad|iPod/.test(navigator.platform) ||
    navigator.userAgent.includes("Mac"));
const modKey = isApplePlatform ? "⌘" : "Ctrl";

type ShortcutRow = { keys: string[]; action: string };
type ShortcutGroup = { title: string; items: ShortcutRow[] };

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "View",
    items: [
      { keys: ["Drag"], action: "Pan empty canvas" },
      { keys: ["Space"], action: "Hold and drag to pan" },
      { keys: ["←", "→", "↑", "↓"], action: "Pan" },
      { keys: ["Shift", "arrows"], action: "Pan farther" },
      { keys: ["Scroll"], action: "Zoom" },
    ],
  },
  {
    title: "Draw",
    items: [
      { keys: ["Enter"], action: "Finish line or river" },
      { keys: ["Escape"], action: "Cancel action; otherwise return to Select" },
      { keys: ["Delete"], action: "Delete selection" },
    ],
  },
  {
    title: "Edit",
    items: [
      { keys: ["Corner drag"], action: "Resize landmark proportionally" },
      { keys: ["Shift", "corner drag"], action: "Stretch landmark freely" },
      { keys: ["Shift", "rotate"], action: "Snap rotation to 15°" },
      { keys: [modKey, "Z"], action: "Undo" },
      { keys: [modKey, "Shift", "Z"], action: "Redo" },
      { keys: ["?"], action: "Show shortcuts" },
    ],
  },
];

let syncingFromProp = false;
let lastEmitted = "";
let resizeObserver: Maybe<ResizeObserver> = nothing;

watch(
  () => model.value,
  (raw) => {
    if (raw === lastEmitted) return;
    lastEmitted = raw;
    syncingFromProp = true;
    const { doc: next, healed } = parseMapDocumentWithHeal(raw);
    doc.value = next;
    cancelDraftLine();
    selection.value = nothing;
    syncingFromProp = false;
    // Heal-on-read: push a default/repaired map back so disk is not left corrupt.
    if (healed === true) {
      syncMapToParent();
    }
    // After parse: Relationships → map focus (load watch clears selection above).
    tryApplyFocusLandmark();
    scheduleDraw();
  },
  { immediate: true },
);

// Same map already open: focus id arrives without a model reload.
watch(
  () => props.focusLandmarkId,
  () => {
    tryApplyFocusLandmark();
  },
);

watch(
  [labelDialogOpen, () => labelCurve.value !== 0],
  async ([open], [wasOpen]) => {
    if (open === false) return;
    const focusEditor =
      wasOpen === false || document.activeElement === labelInput.value;
    await nextTick();
    if (focusEditor) {
      labelInput.value?.focus();
      if (wasOpen === false && isJust(editingLabel.value))
        labelInput.value?.select();
    }
  },
);

/** Push serialized map JSON to the parent so EditorApp can autosave. */
function syncMapToParent(): void {
  if (syncingFromProp) return;
  // Host must only mount when the session already owns this map file. Extra
  // guard: never heal/write into the buffer when we lack a map file name.
  const fileM = props.mapFileName;
  if (
    isNothing(fileM) ||
    fileM.value.length === 0 ||
    isMapFileName(fileM.value) === false
  ) {
    return;
  }
  const json = serializeMapDocument(doc.value);
  if (json === lastEmitted) return;
  lastEmitted = json;
  model.value = json;
}

// Track the serialized snapshot so every nested edit reaches the parent doc ref.
watch(
  () => serializeMapDocument(doc.value),
  () => {
    syncMapToParent();
    scheduleDraw();
  },
);

// Watch the path string (not a Maybe wrapper). Restoring a doc snapshot via
// undo recreates Maybe objects every time; comparing those reloaded the PNG
// from disk and overwrote the correctly restored mask (undo "flash").
watch(
  () => doc.value.landmass?.path ?? "",
  (path) => {
    void loadLandmass(path.length > 0 ? just(path) : nothing);
  },
  { immediate: true },
);

async function loadLandmass(path: Maybe<string>): Promise<void> {
  if (
    isNothing(path) ||
    path.value.length === 0 ||
    isNothing(props.rootPath) ||
    props.rootPath.value.length === 0
  ) {
    setLandmassFromImage(nothing);
    return;
  }
  try {
    const dataUrl = await readProjectAssetDataUrl(
      props.rootPath.value,
      path.value,
    );
    const img = new Image();
    img.onload = () => {
      setLandmassFromImage(just(img));
    };
    img.onerror = () => {
      setLandmassFromImage(nothing);
    };
    img.src = dataUrl;
  } catch {
    // Missing asset is fine for a brand-new landmass path before first stroke.
    setLandmassFromImage(nothing);
  }
}

function setActiveLayer(id: string): void {
  doc.value.activeLayerId = id;
}

async function startRenameLayer(layer: MapLayer): Promise<void> {
  renamingLayerId.value = just(layer.id);
  renameLayerDraft.value = layer.name;
  await nextTick();
  if (isJust(renameLayerInput.value)) renameLayerInput.value.value.focus();
  if (isJust(renameLayerInput.value)) renameLayerInput.value.value.select();
}

function commitRenameLayer(): void {
  const id = renamingLayerId.value;
  if (isNothing(id)) return;
  const name = renameLayerDraft.value.trim();
  renamingLayerId.value = nothing;
  const layer = layerById(id.value);
  if (isNothing(layer) || name.length === 0 || name === layer.value.name)
    return;
  pushUndo();
  layer.value.name = name;
}

function cancelRenameLayer(): void {
  renamingLayerId.value = nothing;
}

function setRenameLayerInput(el: unknown): void {
  renameLayerInput.value =
    el !== null && typeof el === "object" && "focus" in el
      ? just(el as InstanceType<typeof ZwInput>)
      : nothing;
}

const layerDrag = usePointerRowDrag({
  rowAtPoint(x, y) {
    const sidebar = sidebarEl.value;
    if (sidebar === null) return nothing;
    for (const row of sidebar.querySelectorAll<HTMLElement>(
      "[data-map-layer-id]",
    )) {
      const bounds = row.getBoundingClientRect();
      if (
        x >= bounds.left &&
        x <= bounds.right &&
        y >= bounds.top &&
        y <= bounds.bottom
      )
        return toMaybe(row.dataset.mapLayerId);
    }
    return nothing;
  },
  onDrop(from, toKey) {
    if (props.readonly) return;
    pushUndo();
    doc.value.layers = reorderExactSlotByKey(
      doc.value.layers,
      (layer) => layer.id,
      from.key,
      just(toKey),
    );
    scheduleDraw();
  },
});
const layerDragOver = layerDrag.dragOver;
const layerDragFrom = layerDrag.dragFrom;

function setLayerOpacity(layer: MapLayer, value: number): void {
  if (props.readonly) return;
  layer.opacity = value / 100;
  scheduleDraw();
}

function addLayer(): void {
  pushUndo();
  const id = newMapId();
  doc.value.layers.push({
    id,
    name: `Layer ${doc.value.layers.length + 1}`,
    visible: true,
    locked: false,
    nodes: [],
    paths: [],
    fills: [],
    landmarks: [],
    images: [],
  });
  doc.value.activeLayerId = id;
}

function deleteLayer(layer: MapLayer): void {
  if (doc.value.layers.length <= 1) return;
  pushUndo();
  const idx = doc.value.layers.findIndex((entry) => entry.id === layer.id);
  if (idx < 0) return;
  doc.value.layers.splice(idx, 1);
  if (doc.value.activeLayerId === layer.id) {
    const next = doc.value.layers[Math.min(idx, doc.value.layers.length - 1)];
    if (typeof next !== "undefined") doc.value.activeLayerId = next.id;
  }
  if (
    isJust(renamingLayerId.value) &&
    renamingLayerId.value.value === layer.id
  ) {
    cancelRenameLayer();
  }
  scheduleDraw();
}

const landmassVisible = computed(() => {
  const lm = doc.value.landmass;
  return typeof lm === "undefined" || isLandmassVisible(lm);
});

const landmassLocked = computed(() => {
  const lm = doc.value.landmass;
  return typeof lm !== "undefined" && isLandmassLocked(lm);
});

function toggleLandmassVisible(): void {
  const lm = doc.value.landmass;
  if (typeof lm === "undefined") return;
  if (isLandmassVisible(lm)) lm.visible = false;
  else delete lm.visible;
  scheduleDraw();
}

function toggleLandmassLocked(): void {
  const lm = doc.value.landmass;
  if (typeof lm === "undefined") return;
  if (isLandmassLocked(lm)) delete lm.locked;
  else lm.locked = true;
}

const paperVisible = computed(() => isPaperVisible(doc.value));
const paperLocked = computed(() => isPaperLocked(doc.value));
const paperSwatch = computed(() => mapPaperColor(doc.value));
const labelsVisible = computed(() => isLabelsVisible(doc.value));
const labelsLocked = computed(() => isLabelsLocked(doc.value));

function togglePaperVisible(): void {
  if (isPaperVisible(doc.value)) doc.value.paperVisible = false;
  else delete doc.value.paperVisible;
  scheduleDraw();
}

function togglePaperLocked(): void {
  if (isPaperLocked(doc.value)) delete doc.value.paperLocked;
  else doc.value.paperLocked = true;
}

function toggleLabelsVisible(): void {
  if (isLabelsVisible(doc.value)) doc.value.labelsVisible = false;
  else delete doc.value.labelsVisible;
  scheduleDraw();
}

function toggleLabelsLocked(): void {
  if (isLabelsLocked(doc.value)) delete doc.value.labelsLocked;
  else doc.value.labelsLocked = true;
}

function assetDisplayName(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash >= 0 ? path.slice(slash + 1) : path;
}

function uniqueAssetRelativePath(fileName: string): string {
  const base = assetNameFromSource(fileName);
  const existing = new Set(assetList.value);
  const candidate = `${PROJECT_ASSETS_DIR}/${base}`;
  if (existing.has(candidate) === false) return candidate;
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : "";
  let n = 2;
  while (existing.has(`${PROJECT_ASSETS_DIR}/${stem}-${n}${ext}`)) n += 1;
  return `${PROJECT_ASSETS_DIR}/${stem}-${n}${ext}`;
}

async function refreshAssetList(): Promise<void> {
  if (isNothing(props.rootPath) || props.rootPath.value.length === 0) {
    assetList.value = [];
    assetPreviews.value = {};
    return;
  }
  const root = props.rootPath.value;
  try {
    const landmassPath = doc.value.landmass?.path;
    const paths = (await listProjectAssets(root)).filter((path) => {
      if (isMapLandmassAssetPath(path)) return false;
      if (typeof landmassPath === "string" && path === landmassPath)
        return false;
      return true;
    });
    assetList.value = paths;
    const known = new Set(paths);
    const next: Record<string, string> = {};
    for (const [path, src] of Object.entries(assetPreviews.value)) {
      if (known.has(path)) next[path] = src;
    }
    await Promise.all(
      paths.map(async (path) => {
        if (typeof next[path] === "string" && next[path].length > 0) return;
        try {
          next[path] = await readProjectAssetDataUrl(root, path);
        } catch {
          // Missing or unreadable asset: leave the cell empty.
        }
      }),
    );
    assetPreviews.value = next;
  } catch (error) {
    console.error(formatErrorMessage(error, "Failed to list project assets"));
    assetList.value = [];
    assetPreviews.value = {};
  }
}

watch(
  () =>
    [tool.value, isJust(props.rootPath) ? props.rootPath.value : ""] as const,
  ([nextTool]) => {
    if (nextTool === "image") void refreshAssetList();
  },
);

function selectAssetStamp(path: string): void {
  imageStamp.value = just(path);
}

async function uploadMapAsset(): Promise<void> {
  if (
    isNothing(props.rootPath) ||
    props.rootPath.value.length === 0 ||
    assetBusy.value
  )
    return;
  const root = props.rootPath.value;
  assetBusy.value = true;
  try {
    if (isTauri()) {
      const picked = await pickImageFile();
      if (picked === null || picked.length === 0) return;
      const relative = assetRelativePath(
        await importProjectAsset(root, picked, assetNameFromSource(picked)),
      );
      await refreshAssetList();
      imageStamp.value = just(relative);
      return;
    }
    assetUploadInput.value?.click();
  } catch (error) {
    console.error(formatErrorMessage(error, "Failed to add image"));
    window.alert(formatErrorMessage(error, "Could not add image."));
  } finally {
    assetBusy.value = false;
  }
}

async function onAssetFilePicked(event: Event): Promise<void> {
  const input = event.target;
  if (
    input instanceof HTMLInputElement === false ||
    isNothing(props.rootPath) ||
    props.rootPath.value.length === 0
  )
    return;
  const file = input.files?.[0];
  input.value = "";
  if (typeof file === "undefined") return;
  assetBusy.value = true;
  try {
    const relative = uniqueAssetRelativePath(file.name);
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    const dataBase64 = btoa(binary);
    await writeProjectAssetBytes(props.rootPath.value, relative, dataBase64);
    await refreshAssetList();
    imageStamp.value = just(relative);
  } catch (error) {
    console.error(formatErrorMessage(error, "Failed to upload image"));
    window.alert(formatErrorMessage(error, "Could not add image."));
  } finally {
    assetBusy.value = false;
  }
}

async function loadExportAssetDataUrls(
  exportDoc: MapDocument,
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (isNothing(props.rootPath) || props.rootPath.value.length === 0) {
    return out;
  }
  const root = props.rootPath.value;
  const paths = new Set<string>();
  for (const layer of exportDoc.layers) {
    for (const image of layer.images) paths.add(image.path);
  }
  if (
    typeof exportDoc.landmass !== "undefined" &&
    isLandmassVisible(exportDoc.landmass)
  ) {
    paths.add(exportDoc.landmass.path);
  }
  await Promise.all(
    [...paths].map(async (assetPath) => {
      const landCanvas = getLandmassCanvas();
      if (
        typeof exportDoc.landmass !== "undefined" &&
        assetPath === exportDoc.landmass.path &&
        isJust(landCanvas)
      ) {
        out.set(assetPath, canvasToPngDataUrl(landCanvas.value));
        return;
      }
      try {
        out.set(assetPath, await readProjectAssetDataUrl(root, assetPath));
      } catch {
        // Missing asset: skip rather than fail the whole export.
      }
    }),
  );
  return out;
}

async function loadLabelFonts(): Promise<void> {
  await Promise.all(
    Object.values(MAP_LABEL_FONTS).map((family) =>
      document.fonts.load(`${LABEL_BASE_PX}px ${family}`),
    ),
  );
}

async function buildExportSvg(): Promise<string> {
  await loadLabelFonts();
  const exportDoc = cloneMapDocument(doc.value);
  const assets = await loadExportAssetDataUrls(exportDoc);
  return mapToSvg(exportDoc, { bounds: "page", assetDataUrls: assets });
}

async function exportMapImage(): Promise<void> {
  if (isTauri() === false || exportBusy.value) return;
  exportBusy.value = true;
  dismissExportToast();
  try {
    const svg = await buildExportSvg();
    const size = mapSvgPixelSize(doc.value, { bounds: "page" });
    const bytes = await rasterizeSvgToPngBytes(svg, size.width, size.height);
    const defaultName = displayFileName(
      unwrapMaybeOr(props.mapFileName, "Map"),
    );
    reportSavedExport("PNG", await saveMapPng(bytes, defaultName));
  } catch (error) {
    console.error("Failed to export map PNG", error);
    reportExportFailed(formatExportFailure("Could not export the map.", error));
  } finally {
    exportBusy.value = false;
  }
}

async function exportMapSvg(): Promise<void> {
  if (isTauri() === false || exportBusy.value) return;
  exportBusy.value = true;
  dismissExportToast();
  try {
    const svg = await buildExportSvg();
    const defaultName = displayFileName(
      unwrapMaybeOr(props.mapFileName, "Map"),
    );
    reportSavedExport("SVG", await saveMapSvg(svg, defaultName));
  } catch (error) {
    console.error("Failed to export map SVG", error);
    reportExportFailed(formatExportFailure("Could not export the map.", error));
  } finally {
    exportBusy.value = false;
  }
}

watch(theme, async () => {
  await nextTick();
  scheduleDraw();
});

onMounted(() => {
  if (doc.value.page?.fitOnOpen === true) {
    fitMapPage();
    delete doc.value.page.fitOnOpen;
  }
  void loadLabelFonts().then(scheduleDraw, scheduleDraw);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  const shell = toMaybe(shellEl.value);
  if (isJust(shell)) {
    const observer = new ResizeObserver(() => scheduleDraw());
    observer.observe(shell.value);
    resizeObserver = just(observer);
  }
  scheduleDraw();
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("keyup", onKeyUp);
  clearStoryDragCandidate();
  onStoryPointerCancel();
  clearStoryHoverClearTimer();
  if (isJust(resizeObserver)) resizeObserver.value.disconnect();
  if (isJust(landmassPersistTimer)) clearTimeout(landmassPersistTimer.value);
  cleanupMapTools();
});

defineExpose({
  exportMapImage,
  exportMapSvg,
} satisfies MapEditorPort);
</script>

<template>
  <div
    ref="shellEl"
    class="map-editor"
    :class="{ 'map-editor-story-dragging': isJust(storyPointerDrag) }"
  >
    <div
      v-if="isJust(storyPointerDrag)"
      class="map-story-drag-ghost"
      :style="storyDragGhostStyle"
      aria-hidden="true"
    >
      <div class="map-story-drag-node">{{ storyDragGhostIndex }}</div>
      <div class="map-story-drag-ghost-label">
        {{ storyPointerDrag.value.label }}
      </div>
    </div>
    <header
      v-if="readonly === false"
      class="map-toolbar"
      role="toolbar"
      aria-label="Map tools"
    >
      <div class="map-tools">
        <template v-for="item in MAP_TOOLS" :key="item.id">
          <ZwMenu
            v-if="item.id === 'water' || item.id === 'terrain'"
            align="start"
            :menu-label="just(`${item.title} options`)"
          >
            <template #trigger="{ toggle, attrs }">
              <div class="map-tool-group">
                <button
                  type="button"
                  class="map-tool map-tool-icon"
                  :class="{ 'map-tool-active': tool === item.id }"
                  :title="item.title"
                  :aria-label="item.title"
                  :aria-pressed="tool === item.id"
                  @click="setTool(item.id)"
                >
                  <component
                    :is="item.icon"
                    class="size-4"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  class="map-tool map-subtool-trigger"
                  :aria-label="`${item.title} options`"
                  :title="`${item.title} options`"
                  v-bind="attrs"
                  @click="toggle()"
                >
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    aria-hidden="true"
                  >
                    <path
                      d="M1 2.5 4 5.5 7 2.5"
                      fill="none"
                      stroke="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </template>
            <template v-if="item.id === 'water'">
              <ZwMenuItem
                v-for="option in WATER_MODE_OPTIONS"
                :key="option.value"
                :label="option.label"
                :shortcut="waterMode === option.value ? just('✓') : nothing"
                :title="nothing"
                @select="
                  setTool('water');
                  waterMode = option.value;
                "
              />
            </template>
            <template v-else>
              <ZwMenuItem
                v-for="option in TERRAIN_BRUSH_OPTIONS"
                :key="option.value"
                :label="option.label"
                :shortcut="
                  terrainBrushStyle === option.value ? just('✓') : nothing
                "
                :title="nothing"
                @select="
                  setTool('terrain');
                  terrainBrushStyle = option.value;
                "
              />
            </template>
          </ZwMenu>
          <button
            v-else
            type="button"
            class="map-tool map-tool-icon"
            :class="{ 'map-tool-active': tool === item.id }"
            :title="item.title"
            :aria-label="item.title"
            :aria-pressed="tool === item.id"
            @click="setTool(item.id)"
          >
            <component :is="item.icon" class="size-4" aria-hidden="true" />
          </button>
        </template>
      </div>
      <div class="map-toolbar-end">
        <button
          v-if="readonly === false"
          type="button"
          class="map-tool map-tool-icon map-tool-quiet"
          aria-label="Map settings"
          title="Map settings"
          @click="openPageSettings"
        >
          <IconSettings class="size-4" aria-hidden="true" />
        </button>
        <ZwMenu
          align="end"
          :menu-label="just('Export map')"
          :disabled="exportBusy"
        >
          <template #trigger="{ toggle, attrs }">
            <button
              type="button"
              class="map-tool map-tool-icon map-tool-quiet"
              title="Export"
              aria-label="Export"
              :disabled="exportBusy"
              v-bind="attrs"
              @click="toggle()"
            >
              <IconMapExport class="size-4" aria-hidden="true" />
            </button>
          </template>
          <ZwMenuItem
            label="Export PNG"
            :shortcut="nothing"
            :title="nothing"
            :disabled="exportBusy"
            @select="void exportMapImage()"
          />
          <ZwMenuItem
            label="Export SVG"
            :shortcut="nothing"
            :title="nothing"
            :disabled="exportBusy"
            @select="void exportMapSvg()"
          />
        </ZwMenu>
        <button
          type="button"
          class="map-tool map-tool-icon map-tool-quiet"
          title="Shortcuts (?)"
          aria-label="Keyboard shortcuts"
          :aria-pressed="shortcutsOpen"
          @click="toggleShortcuts"
        >
          <IconKeyboard class="size-4" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="map-body">
      <div class="map-canvas-wrap">
        <canvas
          ref="canvasEl"
          class="map-canvas"
          :style="showResizeCursor ? { cursor: resizeCursor } : {}"
          :class="{
            'map-canvas-pan': panning || spaceHeld,
            'map-canvas-resize': showResizeCursor,
            'map-canvas-erase':
              tool === 'erase' &&
              showResizeCursor === false &&
              panning === false &&
              spaceHeld === false,
            'map-canvas-stamp':
              (tool === 'landmark' || tool === 'image') &&
              showResizeCursor === false &&
              panning === false &&
              spaceHeld === false,
            'map-canvas-fill':
              tool === 'fill' &&
              showResizeCursor === false &&
              panning === false &&
              spaceHeld === false,
            'map-canvas-landmass':
              (tool === 'landmass' || tool === 'terrain' || tool === 'water') &&
              showResizeCursor === false &&
              panning === false &&
              spaceHeld === false,
          }"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
          @pointerleave="onPointerLeave"
          @wheel.prevent="onWheel"
          @dblclick="onDblClick"
          @contextmenu="onContextMenu"
        />

        <div
          v-if="isJust(storyHoverCard)"
          class="map-story-hover-anchor"
          :style="storyHoverCardStyle"
          role="dialog"
          :aria-label="storyHoverAriaLabel(storyHoverCard.value)"
        >
          <div
            class="map-story-hover-card"
            @focusin="
              storyHoverHasFocus = true;
              clearStoryHoverClearTimer();
            "
            @focusout="
              storyHoverHasFocus = false;
              scheduleStoryHoverClear();
            "
            @pointerenter="onStoryHoverAnchorEnter"
            @pointerleave="onStoryHoverAnchorLeave"
          >
            <div
              v-if="isJust(storyHoverCardTitle)"
              class="map-story-hover-title"
            >
              {{ storyHoverCardTitle.value }}
            </div>
            <ul
              v-if="storyHoverCard.value.sceneFiles.length > 0"
              class="map-story-hover-list"
            >
              <li v-for="file in storyHoverCard.value.sceneFiles" :key="file">
                <div
                  class="map-story-hover-link"
                  role="button"
                  tabindex="0"
                  title="Drag to move this scene, click to open"
                  @pointerdown="
                    onStoryStartDrag({
                      paths: [file],
                      label: storySceneLabel(file),
                      event: $event,
                      from: {
                        kind: storyHoverCard.value.place.kind,
                        id: storyHoverCard.value.place.id,
                      },
                    })
                  "
                  @click="openStoryScene(file)"
                  @keydown.enter.prevent="openStoryScene(file)"
                >
                  {{ storySceneLabel(file) }}
                </div>
              </li>
            </ul>
            <p v-else class="map-story-hover-empty">No scenes linked yet.</p>
            <div
              v-if="
                storyHoverCard.value.arcMoments.length > 0 ||
                storyHoverCard.value.place.kind === 'marker'
              "
              class="map-story-hover-footer"
            >
              <ul
                v-if="storyHoverCard.value.arcMoments.length > 0"
                class="map-story-hover-chips"
              >
                <li
                  v-for="(arc, i) in storyHoverCard.value.arcMoments"
                  :key="`${arc.entityFile}-${arc.momentLabel}-${i}`"
                >
                  <button
                    type="button"
                    :class="storyArcChipClass(arc)"
                    :style="fromMaybe(storyArcChipStyle(arc))"
                    :title="`Open ${displayFileName(arc.entityFile)}`"
                    @click="openStoryEntity(arc.entityFile)"
                  >
                    {{ storyArcChipLabel(arc) }}
                  </button>
                </li>
              </ul>
              <div
                v-if="storyHoverCard.value.place.kind === 'marker'"
                class="map-story-location-action"
              >
                <ZwSearchableMenu
                  v-model:open="storyLocationMenuOpen"
                  :options="markerLocationOptions"
                  :menu-label="just('Choose location')"
                  search-placeholder="Search locations…"
                  align="end"
                  :disabled="readonly"
                  @select="hoverMarkerLocation = $event"
                >
                  <template #trigger="{ toggle, attrs }">
                    <button
                      type="button"
                      class="map-story-location-button"
                      v-bind="attrs"
                      :disabled="readonly"
                      @pointerdown.stop
                      @click="toggle()"
                    >
                      {{
                        hoverMarkerLocation.length > 0
                          ? displayFileName(hoverMarkerLocation)
                          : "+ Location"
                      }}
                    </button>
                  </template>
                </ZwSearchableMenu>
              </div>
            </div>
          </div>
          <div class="map-story-hover-caret" aria-hidden="true">
            <span class="map-story-hover-caret-border" />
            <span class="map-story-hover-caret-fill" />
          </div>
        </div>

        <textarea
          v-if="labelDialogOpen"
          ref="labelInput"
          v-model="labelDraft"
          class="map-label-editor"
          :style="labelEditorStyle"
          aria-label="Label text"
          placeholder="Label"
          spellcheck="false"
          writingsuggestions="false"
          @keydown.enter="onLabelEnter"
          @keydown.esc.stop.prevent="cancelLabelDialog"
          @pointerdown.stop
        />

        <aside
          ref="sidebarEl"
          class="map-sidebar"
          :class="{
            'map-sidebar-expanded':
              hasToolOptions ||
              isJust(activeLandmark) ||
              (tool === 'landmark' && readonly === false) ||
              (tool === 'image' && readonly === false) ||
              tool === 'storyPath',
          }"
          aria-label="Layers"
        >
          <div class="map-sidebar-head">Layers</div>

          <div class="map-sidebar-stack">
            <div class="map-sidebar-pane">
              <ul class="map-layer-list">
                <li
                  class="map-layer-item map-layer-system"
                  :class="{ 'map-layer-hidden': labelsVisible === false }"
                >
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :title="
                      labelsVisible === true ? 'Hide labels' : 'Show labels'
                    "
                    :aria-label="
                      labelsVisible === true ? 'Hide labels' : 'Show labels'
                    "
                    :aria-pressed="labelsVisible === false"
                    :disabled="readonly"
                    @click.stop="toggleLabelsVisible"
                  >
                    <IconEye
                      v-if="labelsVisible === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconEyeOff v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :class="{
                      'map-layer-icon-btn-on': labelsLocked === true,
                    }"
                    :title="
                      labelsLocked === true ? 'Unlock labels' : 'Lock labels'
                    "
                    :aria-label="
                      labelsLocked === true ? 'Unlock labels' : 'Lock labels'
                    "
                    :aria-pressed="labelsLocked === true"
                    :disabled="readonly"
                    @click.stop="toggleLabelsLocked"
                  >
                    <IconLock
                      v-if="labelsLocked === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconUnlock v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <span class="map-layer-name map-layer-name-static"
                    >Labels</span
                  >
                  <span class="map-layer-icon-spacer" aria-hidden="true" />
                </li>

                <li class="map-layer-section" aria-hidden="true">Map</li>
                <li
                  v-for="layer in doc.layers.toReversed()"
                  :key="layer.id"
                  :data-map-layer-id="layer.id"
                  class="map-layer-item"
                  :class="{
                    'map-layer-active': layer.id === doc.activeLayerId,
                    'map-layer-reorderable': readonly === false,
                    'map-layer-dragging':
                      isJust(layerDragFrom) &&
                      layerDragFrom.value.key === layer.id,
                    'map-layer-drop-target':
                      isJust(layerDragOver) && layerDragOver.value === layer.id,
                    'map-layer-hidden': layer.visible === false,
                  }"
                  @click="setActiveLayer(layer.id)"
                >
                  <button
                    v-if="readonly === false"
                    type="button"
                    class="map-layer-icon-btn map-layer-drag"
                    :aria-label="`Reorder ${layer.name}`"
                    title="Drag to reorder"
                    @pointerdown="
                      layerDrag.onRowPointerDown({ key: layer.id }, $event)
                    "
                    @click.stop
                  >
                    ⠿
                  </button>
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :title="
                      layer.visible === true ? 'Hide layer' : 'Show layer'
                    "
                    :aria-label="
                      layer.visible === true ? 'Hide layer' : 'Show layer'
                    "
                    :aria-pressed="layer.visible === false"
                    :disabled="readonly"
                    @click.stop="toggleLayerVisible(layer)"
                  >
                    <IconEye
                      v-if="layer.visible === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconEyeOff v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :class="{
                      'map-layer-icon-btn-on': layer.locked === true,
                    }"
                    :title="
                      layer.locked === true ? 'Unlock layer' : 'Lock layer'
                    "
                    :aria-label="
                      layer.locked === true ? 'Unlock layer' : 'Lock layer'
                    "
                    :aria-pressed="layer.locked === true"
                    :disabled="readonly"
                    @click.stop="toggleLayerLocked(layer)"
                  >
                    <IconLock
                      v-if="layer.locked === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconUnlock v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <ZwRangePopover
                    v-if="readonly === false"
                    class="map-layer-opacity"
                    :model-value="Math.round((layer.opacity ?? 1) * 100)"
                    :label="`${layer.name} opacity`"
                    :value-text="`${Math.round((layer.opacity ?? 1) * 100)}%`"
                    :min="0"
                    :max="100"
                    :step="1"
                    :dismiss-key="just(doc.activeLayerId)"
                    @click.stop
                    @adjust-start="pushUndo()"
                    @update:model-value="setLayerOpacity(layer, $event)"
                  />
                  <ZwInput
                    v-if="
                      isJust(renamingLayerId) &&
                      renamingLayerId.value === layer.id &&
                      readonly === false
                    "
                    :id="nothing"
                    :ref="setRenameLayerInput"
                    v-model="renameLayerDraft"
                    class="map-layer-rename"
                    variant="plain"
                    aria-label="Layer name"
                    :label="nothing"
                    @click.stop
                    @keydown.enter.prevent="commitRenameLayer"
                    @keydown.escape.prevent="cancelRenameLayer"
                    @blur="commitRenameLayer"
                  />
                  <button
                    v-else
                    type="button"
                    class="map-layer-name"
                    @click.stop="onLayerNameClick(layer)"
                  >
                    {{ layer.name }}
                  </button>
                  <button
                    v-if="readonly === false"
                    type="button"
                    class="map-layer-icon-btn map-layer-delete"
                    title="Delete layer"
                    aria-label="Delete layer"
                    :disabled="doc.layers.length <= 1"
                    @click.stop="deleteLayer(layer)"
                  >
                    <IconTrash class="size-3.5" aria-hidden="true" />
                  </button>
                </li>
                <li v-if="readonly === false" class="map-layer-item-add">
                  <button type="button" class="map-layer-add" @click="addLayer">
                    New layer
                  </button>
                </li>

                <li
                  class="map-layer-item map-layer-system"
                  :class="{ 'map-layer-hidden': landmassVisible === false }"
                >
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :title="
                      landmassVisible === true ? 'Hide land' : 'Show land'
                    "
                    :aria-label="
                      landmassVisible === true ? 'Hide land' : 'Show land'
                    "
                    :aria-pressed="landmassVisible === false"
                    :disabled="readonly || typeof doc.landmass === 'undefined'"
                    @click.stop="toggleLandmassVisible"
                  >
                    <IconEye
                      v-if="landmassVisible === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconEyeOff v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :class="{
                      'map-layer-icon-btn-on': landmassLocked === true,
                    }"
                    :title="
                      landmassLocked === true ? 'Unlock land' : 'Lock land'
                    "
                    :aria-label="
                      landmassLocked === true ? 'Unlock land' : 'Lock land'
                    "
                    :aria-pressed="landmassLocked === true"
                    :disabled="readonly || typeof doc.landmass === 'undefined'"
                    @click.stop="toggleLandmassLocked"
                  >
                    <IconLock
                      v-if="landmassLocked === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconUnlock v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <span class="map-layer-name map-layer-name-static">Land</span>
                  <span class="map-layer-icon-spacer" aria-hidden="true" />
                </li>
                <li
                  class="map-layer-item map-layer-system"
                  :class="{ 'map-layer-hidden': paperVisible === false }"
                >
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :title="paperVisible === true ? 'Hide ocean' : 'Show ocean'"
                    :aria-label="
                      paperVisible === true ? 'Hide ocean' : 'Show ocean'
                    "
                    :aria-pressed="paperVisible === false"
                    :disabled="readonly"
                    @click.stop="togglePaperVisible"
                  >
                    <IconEye
                      v-if="paperVisible === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconEyeOff v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    class="map-layer-icon-btn"
                    :class="{
                      'map-layer-icon-btn-on': paperLocked === true,
                    }"
                    :title="
                      paperLocked === true ? 'Unlock ocean' : 'Lock ocean'
                    "
                    :aria-label="
                      paperLocked === true ? 'Unlock ocean' : 'Lock ocean'
                    "
                    :aria-pressed="paperLocked === true"
                    :disabled="readonly"
                    @click.stop="togglePaperLocked"
                  >
                    <IconLock
                      v-if="paperLocked === true"
                      class="size-3.5"
                      aria-hidden="true"
                    />
                    <IconUnlock v-else class="size-3.5" aria-hidden="true" />
                  </button>
                  <span class="map-layer-name map-layer-name-static">
                    <span
                      class="map-layer-swatch"
                      :style="{ background: paperSwatch }"
                      aria-hidden="true"
                    />
                    Ocean
                  </span>
                  <span class="map-layer-icon-spacer" aria-hidden="true" />
                </li>
              </ul>
            </div>

            <MapStoryPathSidebar
              v-if="tool === 'storyPath'"
              :chapters="manuscriptChapters"
              :is-scene-placed="isStoryScenePlaced"
              :landmark-icon-markup="storyLandmarkIconMarkup"
              @open-scene="openStoryScene"
              @start-drag="onStoryStartDrag"
              @hover-paths="onStorySidebarHoverPaths"
            />

            <section
              v-if="hasToolOptions"
              class="map-tool-options"
              aria-label="Tool options"
            >
              <div class="map-sidebar-head">Tool options</div>
              <div class="map-tool-options-fields">
                <template v-if="showLabelOptions">
                  <ZwMenu :menu-label="just('Label font')">
                    <template #trigger="{ toggle, attrs }">
                      <button
                        type="button"
                        class="zw-dropdown-trigger map-font-trigger"
                        :aria-label="`Font: ${selectedFontName}`"
                        v-bind="attrs"
                        @click="toggle()"
                      >
                        <span class="zw-dropdown-name">Font</span>
                        <span
                          class="zw-dropdown-value"
                          :style="{ fontFamily: MAP_LABEL_FONTS[labelFont] }"
                          >{{ selectedFontName }}</span
                        >
                        <span aria-hidden="true">⌄</span>
                      </button>
                    </template>
                    <ZwMenuItem
                      v-for="font in LABEL_FONT_OPTIONS"
                      :key="font.value"
                      :label="font.label"
                      :title="nothing"
                      :shortcut="nothing"
                      :style="{
                        fontFamily: MAP_LABEL_FONTS[font.value],
                        fontSize: '18px',
                      }"
                      @select="
                        labelFont = font.value;
                        applyLabelAppearance(true);
                      "
                    />
                  </ZwMenu>
                  <ZwRangePopover
                    v-model="labelPixelSize"
                    label="Text size"
                    :value-text="`${labelPixelSize} px`"
                    :min="2"
                    :max="200"
                    :step="1"
                    :dismiss-key="just(tool)"
                    @adjust-start="
                      labelDialogOpen === false &&
                      inkTargetSelected &&
                      pushUndo()
                    "
                  />
                  <ZwRangePopover
                    v-model="labelCurve"
                    label="Bend"
                    :value-text="
                      labelCurve === 0 ? 'Straight' : `${labelCurve}°`
                    "
                    :min="-180"
                    :max="180"
                    :step="5"
                    :dismiss-key="just(tool)"
                    @adjust-start="
                      labelDialogOpen === false &&
                      inkTargetSelected &&
                      pushUndo()
                    "
                    @update:model-value="applyLabelAppearance(false)"
                  />
                  <ZwRangePopover
                    v-model="labelSpacing"
                    label="Letter spacing"
                    :value-text="`${labelSpacing} px`"
                    :min="0"
                    :max="24"
                    :step="0.5"
                    :dismiss-key="just(tool)"
                    @adjust-start="
                      labelDialogOpen === false &&
                      inkTargetSelected &&
                      pushUndo()
                    "
                    @update:model-value="applyLabelAppearance(false)"
                  />
                </template>
                <MapInkPicker
                  v-if="
                    tool === 'line' ||
                    tool === 'landmark' ||
                    tool === 'label' ||
                    (tool === 'select' && inkTargetSelected)
                  "
                  v-model="inkColor"
                  label="Line"
                  @adjust-start="
                    labelDialogOpen === false && inkTargetSelected && pushUndo()
                  "
                  @update:model-value="applyInkColor"
                />
                <MapInkPicker
                  v-if="
                    tool === 'landmass' ||
                    tool === 'terrain' ||
                    tool === 'fill' ||
                    (tool === 'select' && fillTargetSelected)
                  "
                  v-model="fillColor"
                  label="Fill"
                  @adjust-start="fillTargetSelected && pushUndo()"
                  @update:model-value="applyFillColor"
                />
                <template
                  v-if="
                    tool === 'landmass' ||
                    tool === 'terrain' ||
                    tool === 'erase' ||
                    tool === 'water'
                  "
                >
                  <ZwRangePopover
                    v-if="tool === 'terrain' && terrainBrushStyle === 'mottled'"
                    v-model="terrainCoverage"
                    label="Coverage"
                    :value-text="`${terrainCoverage}%`"
                    :min="5"
                    :max="100"
                    :step="5"
                    :dismiss-key="just(tool)"
                  />
                  <ZwRangePopover
                    v-if="tool === 'terrain'"
                    v-model="terrainOpacity"
                    label="Opacity"
                    :value-text="`${terrainOpacity}%`"
                    :min="5"
                    :max="100"
                    :step="5"
                    :dismiss-key="just(tool)"
                  />
                  <ZwRangePopover
                    v-model="landmassBrushSize"
                    label="Size"
                    :value-text="landmassBrushSizeText"
                    :min="LANDMASS_BRUSH_SIZE_MIN"
                    :max="LANDMASS_BRUSH_SIZE_MAX"
                    :step="1"
                    :dismiss-key="just(tool)"
                  />
                  <ZwRangePopover
                    v-if="
                      tool === 'landmass' ||
                      tool === 'water' ||
                      (tool === 'terrain' && terrainBrushStyle !== 'feathered')
                    "
                    v-model="landmassCoastRoughness"
                    label="Roughness"
                    :value-text="landmassCoastRoughnessText"
                    :min="LANDMASS_COAST_ROUGHNESS_MIN"
                    :max="LANDMASS_COAST_ROUGHNESS_MAX"
                    :step="5"
                    :dismiss-key="just(tool)"
                  />
                </template>
                <template v-else>
                  <ZwDropdown
                    v-if="showPathOptions"
                    v-model="inkWeight"
                    label="Weight"
                    :options="[...PATH_WEIGHT_OPTIONS]"
                    @update:model-value="applyInkWeight"
                  />
                  <ZwDropdown
                    v-if="showPathOptions"
                    v-model="inkStyle"
                    label="Line type"
                    :options="[...PATH_STYLE_OPTIONS]"
                    @update:model-value="applyInkStyle"
                  />
                  <ZwRangePopover
                    v-if="
                      tool === 'landmark' &&
                      (isNothing(selection) ||
                        selection.value.kind !== 'landmark')
                    "
                    v-model="sizeSliderModel"
                    :label="objectSizeLabel"
                    :value-text="objectSizeText"
                    :min="objectSizeMin"
                    :max="LANDMARK_SIZE_MAX"
                    :step="Math.min(0.05, objectSizeMin)"
                    :dismiss-key="just(tool)"
                    @adjust-start="pushUndo()"
                  />
                  <button
                    v-if="
                      isJust(selection) &&
                      selection.value.kind === 'node' &&
                      selection.value.pathId.length > 0
                    "
                    type="button"
                    class="map-tool map-tool-quiet"
                    @click="splitSelectedNode"
                  >
                    Split path
                  </button>
                </template>
              </div>
            </section>

            <section
              v-if="tool === 'landmark' && readonly === false"
              class="map-symbol-library"
              aria-label="Landmark symbols"
            >
              <div class="map-sidebar-head">Symbols</div>
              <ZwInput
                :id="nothing"
                v-model="symbolSearch"
                class="map-symbol-search"
                :label="nothing"
                aria-label="Search symbols"
                placeholder="Search symbols…"
              />
              <div class="map-symbol-list">
                <p v-if="symbolGroups.length === 0" class="map-symbol-empty">
                  No matching symbols.
                </p>
                <section v-for="group in symbolGroups" :key="group.label">
                  <h3 class="map-symbol-group-title">{{ group.label }}</h3>
                  <ul class="map-stamp-list">
                    <li v-for="icon in group.icons" :key="icon.id">
                      <button
                        type="button"
                        class="map-stamp-btn"
                        :class="{
                          'map-stamp-active':
                            isJust(landmarkStamp) &&
                            landmarkStamp.value === icon.id,
                        }"
                        :title="icon.label"
                        :aria-label="icon.label"
                        :aria-pressed="
                          isJust(landmarkStamp) &&
                          landmarkStamp.value === icon.id
                        "
                        @click="landmarkStamp = just(icon.id)"
                      >
                        <StaticSvgMarkup
                          v-if="
                            typeof icon.svg === 'string' && icon.svg.length > 0
                          "
                          class="map-stamp-raw"
                          aria-hidden="true"
                          :markup="icon.svg"
                        />
                      </button>
                    </li>
                  </ul>
                </section>
              </div>
            </section>
            <section
              v-else-if="tool === 'image' && readonly === false"
              class="map-symbol-library"
              aria-label="Project assets"
            >
              <div class="map-sidebar-head map-assets-head">
                <span>Assets</span>
                <ZwButton
                  class="map-assets-upload"
                  :disabled="
                    assetBusy ||
                    isNothing(rootPath) ||
                    rootPath.value.length === 0
                  "
                  @click="uploadMapAsset"
                >
                  + Add
                </ZwButton>
              </div>
              <input
                ref="assetUploadInput"
                type="file"
                class="map-assets-file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/avif,.png,.jpg,.jpeg,.gif,.webp,.avif"
                @change="onAssetFilePicked"
              />
              <div class="map-symbol-list">
                <p
                  v-if="isNothing(rootPath) || rootPath.value.length === 0"
                  class="map-symbol-empty"
                >
                  Open a project to use Assets.
                </p>
                <p v-else-if="assetList.length === 0" class="map-symbol-empty">
                  No images in Assets yet. Add one to place on the map.
                </p>
                <ul v-else class="map-asset-list">
                  <li v-for="path in assetList" :key="path">
                    <button
                      type="button"
                      class="map-asset-btn"
                      :class="{
                        'map-stamp-active':
                          isJust(imageStamp) && imageStamp.value === path,
                      }"
                      :title="path"
                      :aria-label="assetDisplayName(path)"
                      :aria-pressed="
                        isJust(imageStamp) && imageStamp.value === path
                      "
                      @click="selectAssetStamp(path)"
                    >
                      <img
                        v-if="
                          typeof assetPreviews[path] === 'string' &&
                          assetPreviews[path].length > 0
                        "
                        class="map-asset-thumb"
                        :src="assetPreviews[path]"
                        alt=""
                        draggable="false"
                      />
                      <span v-else class="map-asset-thumb-fallback">{{
                        assetDisplayName(path)
                      }}</span>
                    </button>
                  </li>
                </ul>
              </div>
            </section>
            <div
              v-else-if="
                isJust(activeLandmark) &&
                (tool === 'select' || tool === 'landmark')
              "
              class="map-sidebar-detail"
            >
              <MapLandmarkPanel
                embedded
                :landmark="activeLandmark.value"
                :readonly="readonly"
                :entity-options="entityOptions"
                :scene-options="sceneOptions"
                :entity-resolved="landmarkEntityResolved"
                :bound-entity-title="boundEntityTitle"
                :bound-entity-body-html="boundEntityBodyHtml"
                @begin-edit="beginLandmarkPanelEdit"
                @update:caption="setLandmarkCaption"
                @update:notes="setLandmarkNotes"
                @update:entity-file="setLandmarkEntityFile"
                @clear-entity="clearLandmarkEntity"
                @open-entry="openLandmarkEntry"
                @add-scene-link="addLandmarkSceneLink"
                @replace-scene-link="replaceLandmarkSceneLink"
                @remove-scene-link="removeLandmarkSceneLink"
                @open-scene="openLandmarkScene"
                @close="closeLandmarkPanel"
              />
            </div>
          </div>
        </aside>
      </div>
    </div>

    <ZwDialog
      :open="pageSettingsOpen"
      title="Map settings"
      :description="
        just(
          'Page size controls the visible map and exported image. Artwork keeps its size and position.',
        )
      "
      @close="pageSettingsOpen = false"
    >
      <div class="map-page-fields">
        <MapPageSizeFields
          v-model:width="pageWidth"
          v-model:height="pageHeight"
        />
        <ZwSelect
          :id="nothing"
          v-model="pageFrame"
          :label="just('Frame')"
          :options="just(MAP_FRAME_OPTIONS)"
        />
        <p>
          Smaller pages crop the view without deleting artwork. Enlarge the page
          to reveal it again.
        </p>
      </div>
      <template #footer>
        <ZwButton @click="pageSettingsOpen = false">Cancel</ZwButton>
        <ZwButton variant="primary" @click="applyPageSettings">Apply</ZwButton>
      </template>
    </ZwDialog>
    <ZwDialog
      :open="shortcutsOpen"
      title="Map shortcuts"
      :description="just('Keys and pointer gestures for the canvas.')"
      size="md"
      show-close
      @close="closeShortcuts()"
    >
      <div class="map-shortcuts">
        <section
          v-for="group in SHORTCUT_GROUPS"
          :key="group.title"
          class="map-shortcuts-group"
        >
          <h3 class="map-shortcuts-heading">{{ group.title }}</h3>
          <ul class="map-shortcuts-list">
            <li
              v-for="item in group.items"
              :key="item.action"
              class="map-shortcuts-row"
            >
              <span class="map-shortcuts-action">{{ item.action }}</span>
              <span class="map-shortcuts-keys">
                <kbd
                  v-for="(key, index) in item.keys"
                  :key="`${item.action}-${index}`"
                  class="map-shortcuts-kbd"
                >
                  {{ key }}
                </kbd>
              </span>
            </li>
          </ul>
        </section>
      </div>
      <template #footer>
        <ZwButton variant="primary" @click="closeShortcuts()"> Close </ZwButton>
      </template>
    </ZwDialog>
  </div>
</template>

<style scoped>
.map-story-hover-footer {
  display: flex;
  align-items: flex-start;
  gap: var(--space-lg);
  margin-top: var(--space-sm);
}
.map-story-location-action {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  margin-left: auto;
}
.map-story-location-action :deep(.zw-searchable-menu) {
  width: auto;
}
.map-story-location-button {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-family: var(--font-prose);
  font-size: 0.7rem;
  line-height: 1.25;
  cursor: pointer;
  padding: calc(0.15rem + 1px) 0;
}
.map-story-location-button:hover {
  color: var(--text);
}
.map-story-location-button:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  border-radius: 0.25rem;
}

.map-page-fields {
  display: grid;
  gap: 0.75rem;
}
.map-page-fields p {
  color: var(--text-muted);
  font-size: 0.8rem;
}
.map-font-trigger {
  justify-content: space-between;
}

.map-label-editor {
  position: absolute;
  z-index: 1;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  resize: none;
  overflow: hidden;
  margin: 0;
  padding: 4px 8px;
  border: 1px dashed var(--comment);
  border-radius: 2px;
  outline: none;
  background: transparent;
  text-align: center;
  white-space: pre;
}

/* Anchor at the place; caret is visual-only. Only the card captures pointer
   so the node underneath stays draggable. */
.map-story-hover-anchor {
  position: absolute;
  z-index: 3;
  transform: translate(-50%, -100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.map-story-hover-card {
  box-sizing: border-box;
  min-width: 10rem;
  width: max-content;
  max-width: min(28rem, calc(100vw - 2rem));
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg-raised);
  color: var(--text);
  pointer-events: auto;
}

.map-story-hover-caret {
  position: relative;
  width: 1.35rem;
  height: 0.85rem;
  flex-shrink: 0;
  pointer-events: none;
}

.map-story-hover-caret-border,
.map-story-hover-caret-fill {
  position: absolute;
  left: 50%;
  top: 0;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border-left: 0.6rem solid transparent;
  border-right: 0.6rem solid transparent;
  pointer-events: none;
}

.map-story-hover-caret-border {
  border-top: 0.85rem solid var(--line);
}

.map-story-hover-caret-fill {
  top: -1px;
  border-left-width: 0.5rem;
  border-right-width: 0.5rem;
  border-top: 0.72rem solid var(--bg-raised);
}

.map-story-hover-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 0.35rem;
}

.map-story-hover-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.map-story-hover-chips {
  list-style: none;
  margin: 0;
  min-width: 0;
  flex: 1;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.map-story-hover-chips > li {
  display: flex;
  align-items: flex-start;
}

.map-story-hover-chip {
  appearance: none;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.7rem;
  line-height: 1.25;
  padding: 0.15rem 0.55rem;
  border-radius: 1rem;
  border: 1px solid var(--line);
  color: var(--text-muted);
  max-width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;
  background: var(--bg);
  cursor: pointer;
}

.map-story-hover-chip-default {
  border-color: color-mix(in oklch, var(--color-zw-amber) 50%, var(--line));
  color: color-mix(in oklch, var(--color-zw-amber) 70%, var(--text-muted));
  background: color-mix(in oklch, var(--color-zw-amber) 12%, var(--bg));
}

.map-story-hover-chip:hover {
  border-color: color-mix(in oklch, var(--color-zw-amber) 55%, var(--line));
  color: var(--text);
}

.map-story-hover-link {
  box-sizing: border-box;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 0.2rem;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 0.8125rem;
  padding: 0.2rem 0.3rem;
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.map-story-hover-link:hover {
  background: var(--hover-fill);
  border-color: var(--line);
}

.map-story-hover-empty {
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.map-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--bg);
  font-family: var(--font-ui);
}

.map-editor-story-dragging {
  cursor: grabbing;
}

.map-editor-story-dragging .map-canvas {
  cursor: copy;
}

.map-story-drag-ghost {
  position: fixed;
  z-index: 40;
  transform: translate(-50%, -50%);
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.map-story-drag-node {
  box-sizing: border-box;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  border: 2.5px solid rgba(250, 248, 240, 0.95);
  outline: 1.25px solid rgba(40, 32, 24, 0.55);
  background: var(--map-story-drag-node, #7a4a2e);
  color: #faf8f0;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.25rem;
  text-align: center;
}

.map-story-drag-ghost-label {
  box-sizing: border-box;
  max-width: 12rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg-raised);
  color: var(--text);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.map-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0.75rem;
  border-bottom: 1px solid var(--line);
  background: var(--bg-raised);
  flex-wrap: wrap;
}

.map-tools,
.map-toolbar-end {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.map-tool-options {
  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
  border-bottom: 1px solid var(--line);
}

.map-tool-options-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
}

.map-tool-options-fields :deep(.zw-range-popover),
.map-tool-options-fields :deep(.zw-menu),
.map-tool-options-fields :deep(.zw-range-popover-trigger),
.map-tool-options-fields :deep(.zw-dropdown-trigger) {
  box-sizing: border-box;
  width: 100%;
}

.map-tool-options-fields :deep(.zw-range-popover-value),
.map-tool-options-fields :deep(.zw-dropdown-value) {
  margin-left: auto;
}

.map-tools {
  padding-right: 0.55rem;
  margin-right: 0.2rem;
  border-right: 1px solid var(--line);
}

.map-toolbar-end {
  margin-left: auto;
  padding-left: 0.55rem;
  border-left: 1px solid var(--line);
}

.map-tool {
  appearance: none;
  border: 1px solid var(--line);
  background: var(--bg);
  color: var(--text);
  border-radius: 0.25rem;
  padding: 0.25rem 0.55rem;
  font-size: 0.8125rem;
  cursor: pointer;
}

.map-tool-group {
  display: inline-flex;
}

.map-tool-group > .map-tool-icon {
  border-radius: 0.25rem 0 0 0.25rem;
}

.map-subtool-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.9rem;
  padding: 0;
  border-left: 0;
  border-radius: 0 0.25rem 0.25rem 0;
}

.map-tool-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  padding: 0;
}

.map-tool:hover:not(:disabled) {
  background: var(--hover-fill);
}

.map-tool:disabled {
  opacity: 0.4;
  cursor: default;
}

.map-tool-active {
  border-color: color-mix(in oklch, var(--comment) 55%, var(--line));
  color: var(--text-strong);
  background: var(--hover-fill);
}

.map-tool-quiet {
  border-color: transparent;
  background: transparent;
}

.map-tool-quiet:hover:not(:disabled) {
  background: var(--hover-fill);
}

.map-tool-quiet.map-tool-active {
  border-color: color-mix(in oklch, var(--comment) 55%, var(--line));
}

.map-ink-field {
  display: inline-flex;
}

.map-color-input {
  width: 2rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: transparent;
}

.map-body {
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
}

.map-canvas-wrap {
  position: relative;
  height: 100%;
  min-height: 0;
  min-width: 0;
}

.map-sidebar {
  position: absolute;
  top: -1px;
  right: 0;
  z-index: 2;
  box-sizing: border-box;
  width: 16.5rem;
  max-height: 15.625rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 0;
  border-left: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  border-top: 1px solid var(--line);
  background: var(--bg-raised);
  border-radius: 0 0 0 0.25rem;
}

.map-sidebar-expanded {
  bottom: 0;
  max-height: none;
  border-bottom: 0;
  border-radius: 0;
}

.map-sidebar-head {
  flex-shrink: 0;
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid var(--line);
  color: var(--text-muted);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.map-sidebar-stack {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.map-sidebar-expanded .map-sidebar-stack {
  flex: 1;
}

.map-sidebar-pane {
  overflow: auto;
  padding: 0;
}

.map-sidebar-expanded .map-sidebar-pane {
  flex: 0 1 auto;
  max-height: 45%;
  border-bottom: 1px solid var(--line);
}

.map-sidebar-stack > .map-story-sidebar {
  flex: 1;
  min-height: 0;
}

.map-sidebar-detail {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.map-canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
}

.map-canvas-pan {
  cursor: grab;
}

.map-canvas-resize {
  cursor: nwse-resize;
}

.map-canvas-erase,
.map-canvas-stamp,
.map-canvas-fill,
.map-canvas-landmass {
  cursor: crosshair;
}

.map-layer-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.map-layer-item {
  display: grid;
  grid-template-columns: 1.75rem 1.75rem minmax(0, 1fr) 1.75rem;
  align-items: center;
  min-height: 2rem;
  border-bottom: 1px solid var(--line);
  background: transparent;
  color: var(--text);
  cursor: default;
}

.map-layer-item[role="button"] {
  cursor: pointer;
}

.map-layer-item[role="button"]:hover {
  background: var(--hover-fill);
}

.map-layer-item:last-of-type {
  border-bottom: 0;
}

.map-layer-system {
  border-top: 1px solid var(--line);
}

.map-layer-active {
  background: var(--hover-fill);
  color: var(--text-strong);
}

.map-layer-hidden .map-layer-name {
  color: var(--text-muted);
}

.map-layer-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.map-layer-icon-btn:hover:not(:disabled) {
  color: var(--text);
  background: color-mix(in oklab, var(--hover-fill) 70%, transparent);
}

.map-layer-icon-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.map-layer-icon-btn-on {
  color: var(--text);
}

.map-layer-name {
  min-width: 0;
  margin: 0;
  padding: 0.35rem 0.55rem 0.35rem 0.2rem;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.8125rem;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: text;
}

.map-layer-name-static {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: default;
}

.map-layer-swatch {
  display: inline-block;
  width: 0.7rem;
  height: 0.7rem;
  flex-shrink: 0;
  border: 1px solid var(--line);
  border-radius: 0.1rem;
}

.map-layer-name:hover {
  color: var(--text-strong);
}

.map-layer-name-static:hover {
  color: inherit;
}

.map-layer-icon-spacer {
  width: 1.75rem;
  height: 1.75rem;
}

.map-layer-delete:hover:not(:disabled) {
  color: var(--danger, #a0442e);
}

.map-layer-rename {
  min-width: 0;
  margin: 0 0.35rem 0 0.15rem;
  box-sizing: border-box;
}

.map-layer-section {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  min-height: 1.5rem;
  padding: 0.35rem 0.75rem 0.15rem;
  border-top: 1px solid var(--line);
  border-bottom: 0;
  color: var(--text-muted);
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.map-layer-item-add {
  display: block;
  border-bottom: 1px solid var(--line);
}

.map-layer-add {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0.4rem 0.75rem 0.45rem 3.7rem;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: 0.75rem;
  text-align: left;
  cursor: pointer;
}

.map-layer-add:hover {
  color: var(--text);
  background: var(--hover-fill);
}

.map-symbol-library {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.map-assets-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.map-assets-upload {
  flex-shrink: 0;
  font-size: 0.75rem;
}

.map-assets-file {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.map-asset-list {
  list-style: none;
  margin: 0;
  padding: 0 0.15rem 0.75rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem;
}

.map-asset-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  margin: 0;
  padding: 0.3rem;
  border: 2px solid transparent;
  border-radius: 0.25rem;
  background: #f7f4ee;
  color: #3d3630;
  font: inherit;
  font-size: 0.65rem;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  overflow: hidden;
}

.map-asset-btn:hover {
  background: #ebe6dc;
}

.map-asset-btn.map-stamp-active {
  border-color: #94633b;
  background: #f0e2cb;
}

.map-asset-btn:focus-visible {
  outline: 2px solid var(--text);
  outline-offset: 1px;
}

.map-asset-thumb {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  pointer-events: none;
}

.map-asset-thumb-fallback {
  display: -webkit-box;
  overflow: hidden;
  padding: 0.15rem;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  word-break: break-all;
}

.map-symbol-search.zw-input-shell {
  margin: 0.4rem 0.65rem 0.65rem;
  width: calc(100% - 1.3rem);
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  height: 1.9rem;
  min-height: 1.9rem;
}
.map-symbol-search.zw-input-shell :deep(.zw-input),
.map-symbol-search.zw-input-shell :deep(.zw-input-face),
.map-symbol-search.zw-input-shell :deep(.zw-input-face-text) {
  font-size: 0.8rem;
  line-height: calc(1.9rem - 2px);
}
.map-symbol-search.zw-input-shell :deep(.zw-input),
.map-symbol-search.zw-input-shell :deep(.zw-input-face) {
  padding: 0 0.45rem;
}
.map-symbol-empty {
  padding: 0.5rem;
  color: var(--text-muted);
}

.map-symbol-list {
  overflow-y: auto;
  overscroll-behavior: contain;
  min-height: 0;
  padding: 0 0.4rem 0.4rem;
}

.map-symbol-group-title {
  position: sticky;
  top: 0;
  margin: 0;
  padding: 0.6rem 0.35rem;
  background: var(--bg-raised);
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 600;
}

.map-stamp-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.35rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.map-stamp-btn {
  border: 2px solid transparent;
  background: #f7f4ee;
  width: 100%;
  height: 4rem;
  padding: 0.35rem;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #3d3630;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.map-stamp-btn:hover {
  background: #ebe6dc;
}

.map-stamp-active {
  border-color: #94633b;
  background: #f0e2cb;
}

.map-stamp-btn:focus-visible {
  outline: 2px solid var(--text);
  outline-offset: 1px;
}

.map-stamp-raw {
  width: 100%;
  height: 100%;
  flex-shrink: 0;
}

.map-stamp-raw :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.map-shortcuts {
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
}

.map-shortcuts-heading {
  margin: 0 0 0.35rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.map-shortcuts-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.map-shortcuts-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.32rem 0;
  border-bottom: 1px solid var(--line);
}

.map-shortcuts-row:last-child {
  border-bottom: 0;
}

.map-shortcuts-action {
  font-size: 0.9rem;
  color: var(--text);
}

.map-shortcuts-keys {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.25rem;
}

.map-shortcuts-kbd {
  display: inline-block;
  min-width: 1.2em;
  padding: 0.08em 0.32em;
  border: 1px solid var(--line);
  border-radius: 0.2rem;
  background: var(--bg);
  font-family: var(--font-ui);
  font-size: 0.75rem;
  line-height: 1.3;
  text-align: center;
  color: var(--text-muted);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>

<style scoped>
.map-layer-reorderable {
  grid-template-columns: 1.25rem 1.75rem 1.75rem 2.5rem minmax(0, 1fr) 1.75rem;
}
.map-layer-drag {
  width: 1.25rem;
  cursor: grab;
  touch-action: none;
}
.map-layer-drop-target {
  outline: 1px dashed
    color-mix(in oklch, var(--color-zw-amber) 55%, transparent);
  outline-offset: -2px;
}
.map-layer-dragging {
  opacity: 0.55;
}
.map-layer-opacity {
  min-width: 0;
  justify-self: center;
}
.map-layer-opacity :deep(.zw-range-popover-value) {
  min-width: 0;
  margin: 0;
  text-align: center;
  color: inherit;
  font-weight: 500;
}
.map-layer-opacity :deep(.zw-range-popover-trigger) {
  width: 2.25rem;
  min-width: 0;
  height: 1.75rem;
  gap: 0;
  padding: 0;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.625rem;
  font-variant-numeric: tabular-nums;
}
.map-layer-opacity :deep(.zw-range-popover-name),
.map-layer-opacity :deep(.zw-range-popover-caret) {
  display: none;
}
.map-layer-opacity :deep(.zw-range-popover-trigger:hover) {
  background: var(--hover-fill);
  color: var(--text-strong);
}
</style>
